import React, {useContext, useEffect, useState, useCallback, useRef, useMemo} from "react";
import { useTranslation, Trans } from 'react-i18next';

import {MapContainer, TileLayer, useMap, useMapEvents, Marker, Popup, MapContainerProps, Rectangle, ZoomControl, Polyline } from "react-leaflet";
import { Box, Paper, Typography, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LatLng, LatLngBounds, LatLngExpression, LatLngBoundsExpression } from 'leaflet';
import { opsColors } from '../../theme/opsTokens';

import { AppContext, Point, Polygon, Line, Layer, Shape, IconType, LocationSettingsContext, TripImportSquishMeta } from "../../context/context";
import { useAddPoint } from "./functions/map_point_functions";
import { useRenderMarkers, renderMarkersWithPick } from "./functions/map_point_functions";
import { useAddPolygon, useRenderPolygons, useRenderTempPolygon } from "./functions/map_polygon_functions";
import { useAddLine, useRenderLines, useRenderTempLine } from "./functions/map_line_functions";
import { useFixedRoutesSync, useRenderFixedRouteStopMarkers } from "./functions/fixedRoutesFunctions";
import { migrateMissionControlTripStyles, isTripLineShape } from "./functions/missionControlTripStyles";
import TripLayerRenderer, { TRIP_RENDER_CAP } from "./components/TripLayerRenderer";
import OsmRouteLayerRenderer from "./components/OsmRouteLayerRenderer";
import MarkersLayer from "./components/MarkersLayer";
import { HighlightProvider } from "../../context/HighlightContext";
import { useFixedRoutes } from "../../context/FixedRoutesContext";
import { optimizeRoute, type OptimizationResult } from "../../utils/fixedRoutes/optimizeRoute";
import { calculateTravelTimeAndDistance, calculateTravelTimesAndDistancesBatch } from "../../services/routing/mapboxClient";
import type { FixedRouteSegment, FixedRouteStop } from "../../types/fixedRoutes";
import { computeRouteGeometryHash } from "../../utils/fixedRoutes/routeToLineConverter";
import RouteOptimizationModal from "./modals/RouteOptimizationModal";
import LayersPanel from '../layer_panel/layersPanel';

import { v4 as uuid4 } from "uuid";
import { useEditLayer } from "../layer_panel/functions/layerFunctions";

import { Menu, MenuItem } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import L from 'leaflet';
import JSZip from 'jszip';
import * as Papa from 'papaparse';
import type { ParseResult, ParseError } from 'papaparse';
import MapToolBar from '../layer_panel/mapToolBar';
import ImportKMLShapeModal from './modals/ImportKMLShapeModal';
import LinearProgress from '@mui/material/LinearProgress';
import ImportOSMLocationsModal from './modals/ImportOSMLocationsModal';
import IsochroneModal from './modals/IsochroneModal';
import { getIsochrone } from '../../services/openRouteService';
import GenerateMeetingPointsModal from './modals/GenerateMeetingPointsModal';
import { snapPointsToRoadsMapbox } from '../../services/mapboxSnapToRoads';
import { defaultLayer } from '../../context/defaults';
import { getDefaultRequestTime } from '../../utils/dateTimeUtils';
import { lineAddressPlaceholders } from '../../utils/rides/addressPlaceholders';
import TransitScanModal from './modals/TransitScanModal';
import * as turf from '@turf/turf';
import difference from '@turf/difference';
import { basemapOptions } from './basemapOptions';
import { MAP_UI_CORNER_INSET_PX, MAP_UI_TOP_BAR_HEIGHT_PX } from './mapUiLayout';
import GoogleTileLayer from './GoogleTileLayer';
import { useGoogleBasemap } from '../../services/googleTiles/useGoogleBasemap';
import {
    GOOGLE_BASEMAP_KEYS,
    alternateGoogleMapType,
    basemapForGoogleMapType,
    googleMapTypeForBasemap,
} from '../../services/googleTiles/basemap';
import type { GoogleMapType } from '../../services/googleTiles/session';
import {
    calculateDistance,
    getDistanceMeters,
    getDistanceKm,
    pointInPolygon,
    convexHull,
    fetchRoadWaysInPolygon,
    samplePointsAlongWay,
    findNearestNode
} from './utils';
import { dropzoneStyle } from './utils/styles';
import { 
    processKMLContent, 
    processOSMBusData,
    OSM_ICON_MAP,
    generateGridPoints,
    getTravelTimesForGrid,
    callGoogleRoutingAPI,
    batchCallGoogleRoutingAPI
} from './processors';
import { classifyPointsByTravelTime, generateSimplePolygons, generateBetterPolygons } from './processors/transit';

import {
    useKeyboardHandlers,
    getNextQuickStartHubName,
    useMapClickHandler,
    useContextMenuHandlers,
    useDragDropHandlers,
    handleImportTrips,
    handleImportKMLShapes,
    handleCreateIsochrone,
    handleGenerateMeetingPoints,
    handleRunTransitScan,
    handleGTFSImport
} from './handlers';
import { handleOsmLocationsImport as rawHandleOsmLocationsImport, handleOsmBusDataImport } from './handlers/osmHandlers';
import { useTransitScan } from './handlers/transit/useTransitScan';

import ErrorMessageBox from './components/ErrorMessageBox';

// Import new components
import { MapEvents } from './components/MapControls';
import DrawingInstructions from './components/DrawingInstructions';

import { useMapEventHandlers } from './handlers/events/mapEventHandlers';

import FleetMenu, { Fleet } from './components/FleetMenu';

import FleetVehicleModal from './components/FleetVehicleModal';
import { customMarker } from './functions/map_point_functions';
import { Vehicle } from '../../types/vehicle';
import SimulationModal from './modals/SimulationModal';

import SimulationResultsMenu from './components/SimulationResultsMenu';

import GenerateRidesDrawer from './components/GenerateRidesDrawer';
import DataTransferDrawer from './components/DataTransferDrawer';
import BugReportDrawer from './components/BugReportDrawer';

import AttributeTableDrawer from '../layer_panel/AttributeTableDrawer';
import { handleImportedShapes } from '../layer_panel/layersPanel';
import { FeatureGate } from '../common/FeatureGate';
import ImportLayerModal from '../layer_panel/ImportLayerModal';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { createSpatialAnalysisTicket } from '../../services/agent/spatialAnalysisTicketService';
import MapSidebar from './components/MapSidebar';
import MapModalsManager from './components/MapModalsManager';
import MapContextMenusManager from './components/MapContextMenusManager';
import SpatialAnalysisDrawer from './components/SpatialAnalysisDrawer';
import HostedNetworkDisplay from './components/HostedNetworkDisplay';
import RouteOverlapMapLayers from './components/RouteOverlapMapLayers';
import HostedNetworkPickerDrawer from './components/HostedNetworkPickerDrawer';
import NetworkFeedDropHandler from './components/NetworkFeedDropHandler';
import { useNetworkBrowse } from '../../context/NetworkBrowseContext';
import { useForkNetworkRoute } from '../../hooks/useForkNetworkRoute';
import { useAuth } from '../../hooks/useAuth';
import { getNetworkPreference } from '../../services/network/networkPreferenceService';
import { useSpatialAnalysis } from '../../context/SpatialAnalysisContext';
import { convertSpatialLayersToMapLayers, isSpatialAnalysisLayerId, mergedSpatialLayerId, TRANSIT_SUPPLY_ROLE } from './utils/spatialAnalysisGeojson';
import { getSpatialAnalysisGtfsFeed } from '../../services/spatialAnalysis/spatialAnalysisApi';
import { convertGTFSFeedToFixedRoutes, GTFSFeedData } from '../../utils/fixedRoutes/gtfsConverter';
import FixedRouteContextMenu from './components/FixedRouteContextMenu';
import RouteStyleDialog from './modals/RouteStyleDialog';
import { onDropMapFile } from "./handlers/drag-drop";

import {
  handleAddOrEditVehicle,
  handleEditVehicle,
  handleAddVehicle,
  handlePickStart,
  handlePickEnd,
  handlePointPick
} from './functions/map_fleet_handlers';
import { resolveWizardTripLayerTarget, upsertWizardFleetList } from './functions/wizardStateUtils';

import HeatmapLayer, { HeatmapLegend } from './components/HeatmapLayer';
import { RasterShape } from '../../types';
import PopulationAnalysisDrawer from '../layer_panel/PopulationAnalysisDrawer';
import { handleTransitAccessibilityAnalysis } from './handlers/transitAccessibilityHandler';
import { secondaryPillButtonSx } from '../../theme/componentStyles';

import { getAtlasApiBaseUrl } from '../../services/getAtlasApiBaseUrl';
import { atlasFetch } from '../../services/api/atlasBackendClient';
import { getApiErrorDetail } from '../../utils/apiErrorDetail';
import WizardModal from '../onboarding-wizard/WizardModal';
import type { WizardEntrypoint } from '../../services/eventTypes';
import { liftangoBrandTokens } from '../../theme';
import type {
  DemandInsightResult,
  WizardFleetHelpResult,
  HubWeight,
  ServicePreset,
  WizardFleetConfigPayload,
  WizardGenerateTripsPayload,
  WizardRunPayload,
} from '../onboarding-wizard/wizardTypes';
import {
  getCanonicalTripLines,
  isCanonicalTripLineShape,
  selectWizardTripLayer,
} from './components/auto-fleet-generator/tripAnalysis';
import { parseTime } from './components/auto-fleet-generator/timeUtils';
import { generateFleetFromTripLayerAnalysis } from './components/auto-fleet-generator/tripLayerFleetGeneration';
import { generateTripsFromAgentAnswers } from '../../services/agent/tripGeneration';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { useSimulation } from '../../context/SimulationContext';
import { useSimulationResultsContext } from '../../context/SimulationResultsContext';
import { simulationService, SimulationService } from '../../services/simulation/simulationService';
import { mapGeneratedRidesToApi } from '../../services/simulation/mappers';
import { mapFleetToApiFleetSettings, buildTimeWindowSettings } from '../../services/simulation/orchestrator';
import type { SimulationRunSummary } from '../../types/simulation';
import { useFeatureFlags } from '../../services/featureFlags/useFeatureFlags';
import { ENVIRONMENT_TOKENS_FLAG, isPanelRuntimeEnabled } from '../../services/featureFlags/runtimeFeatureSets';
import { emitAutomationEvent, updateAgentContract } from '../../automation/agentAutomation';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
// Define a type for our cache Map to fix TypeScript errors
type StringKeyMap<T> = Map<string, T>;

// Define the type for API response
type TimeResult = { index: number; time: number };

type AttributeTableHistoryEntry = {
  layerId: number;
  shapeId: string;
  previousShape: any;
  nextShape: any;
};

// Add new type for dropped file
type DroppedFileType = {
    file: File;
    type: 'excel' | 'gtfs' | 'kml';
};

interface MapComponentProps {
    children?: React.ReactNode;
    registerSimulationModalOpen?: (handler: (() => void) | null) => void;
}

// TypeScript declaration for Google Maps JS API
declare global {
    interface Window {
        google: any;
        gmapsLoadedCallback?: () => void;
    }
}

// Add MapRecenter component
interface MapRecenterProps {
  center: LocationSettingsContext;
  zoom: number;
}
function MapRecenter({ center, zoom }: MapRecenterProps) {
  const map = useMap();
  const lastAppliedRef = useRef<{ lat: number; lng: number; zoom: number } | null>(null);
  useEffect(() => {
    if (center && zoom) {
      const targetZoom = center.zoom || zoom;
      const current = map.getCenter();
      const currentZoom = map.getZoom();
      const isAlreadyAtTarget =
        Math.abs(current.lat - center.latitude) < 1e-7 &&
        Math.abs(current.lng - center.longitude) < 1e-7 &&
        currentZoom === targetZoom;
      if (isAlreadyAtTarget) return;

      const last = lastAppliedRef.current;
      const sameAsLastApplied =
        last &&
        Math.abs(last.lat - center.latitude) < 1e-7 &&
        Math.abs(last.lng - center.longitude) < 1e-7 &&
        last.zoom === targetZoom;
      if (sameAsLastApplied) return;

      lastAppliedRef.current = {
        lat: center.latitude,
        lng: center.longitude,
        zoom: targetZoom,
      };
      map.setView([center.latitude, center.longitude], targetZoom, { animate: false });
    }
  }, [center, zoom, map]);
  return null;
}

interface MapViewportSyncProps {
  onViewportChange: (center: LocationSettingsContext | ((prev: LocationSettingsContext) => LocationSettingsContext)) => void;
}

function MapViewportSync({ onViewportChange }: MapViewportSyncProps) {
  const lastViewportRef = useRef<LocationSettingsContext | null>(null);
  const pendingViewportRef = useRef<LocationSettingsContext | null>(null);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleViewportSync = (next: LocationSettingsContext) => {
    pendingViewportRef.current = next;
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = setTimeout(() => {
      if (!pendingViewportRef.current) return;
      onViewportChange(pendingViewportRef.current);
      pendingViewportRef.current = null;
      syncTimeoutRef.current = null;
    }, 160);
  };
  const map = useMapEvents({
    movestart: () => {
      // If a second drag starts quickly, cancel pending viewport write from the previous drag.
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      pendingViewportRef.current = null;
    },
    moveend: () => {
      const center = map.getCenter();
      const next: LocationSettingsContext = {
        latitude: center.lat,
        longitude: center.lng,
        zoom: map.getZoom(),
      };
      const last = lastViewportRef.current;
      if (
        last &&
        Math.abs(last.latitude - next.latitude) < 1e-8 &&
        Math.abs(last.longitude - next.longitude) < 1e-8 &&
        last.zoom === next.zoom
      ) {
        return;
      }
      lastViewportRef.current = next;
      scheduleViewportSync(next);
    },
    zoomend: () => {
      const center = map.getCenter();
      const next: LocationSettingsContext = {
        latitude: center.lat,
        longitude: center.lng,
        zoom: map.getZoom(),
      };
      const last = lastViewportRef.current;
      if (
        last &&
        Math.abs(last.latitude - next.latitude) < 1e-8 &&
        Math.abs(last.longitude - next.longitude) < 1e-8 &&
        last.zoom === next.zoom
      ) {
        return;
      }
      lastViewportRef.current = next;
      scheduleViewportSync(next);
    },
  });

  useEffect(() => {
    const center = map.getCenter();
    lastViewportRef.current = {
      latitude: center.lat,
      longitude: center.lng,
      zoom: map.getZoom(),
    };
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [map]);

  return null;
}

interface CapAlertProps {
    layerId: number;
    layerName: string;
    tripCount: number;
    onRenderAll: (layerId: number) => void;
    tripImportSquish?: TripImportSquishMeta;
}

/** Auto-dismisses after 8 s; can also be closed manually via the × button. */
function CapAlert({ layerId, layerName, tripCount, onRenderAll, tripImportSquish }: CapAlertProps) {
    const { t } = useTranslation('mapCore');
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(false), 8000);
        return () => clearTimeout(timer);
    }, [layerId]);

    if (!visible) return null;

    return (
        <Alert
            severity="info"
            sx={{ pointerEvents: 'auto', boxShadow: 3 }}
            onClose={() => setVisible(false)}
            action={
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                        if (window.confirm(
                            t('mapShell.tripPerfWarning.confirmRenderAll', { total: tripCount.toLocaleString() })
                        )) {
                            onRenderAll(layerId);
                        }
                    }}
                >
                    {t('mapShell.tripPerfWarning.renderAll')}
                </Button>
            }
        >
            <Typography component="div" variant="body2">
                <Trans i18nKey="mapShell.tripPerfWarning.summary" ns="mapCore" values={{ layerName, shown: TRIP_RENDER_CAP.toLocaleString(), total: tripCount.toLocaleString() }} components={[<strong />]} />
            </Typography>
            <Typography component="div" variant="body2" sx={{ mt: 0.5 }}>
                {t('mapShell.tripPerfWarning.allIncluded', { total: tripCount.toLocaleString() })}
            </Typography>
            {tripImportSquish?.enabled &&
                tripImportSquish.sourceRowCount > tripImportSquish.resultTripCount && (
                    <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                        {t('mapShell.tripPerfWarning.squish', {
                            sourceRows: tripImportSquish.sourceRowCount.toLocaleString(),
                            resultTrips: tripImportSquish.resultTripCount.toLocaleString(),
                            merged: (tripImportSquish.sourceRowCount - tripImportSquish.resultTripCount).toLocaleString()
                        })}
                    </Typography>
                )}
        </Alert>
    );
}

// Above this study-area size, a spatial-analysis run is slow + heavy (large
// Overpass query, raster mask, multi-MB GTFS feed clipped in-process), so we
// confirm before starting. Override with REACT_APP_SPATIAL_ANALYSIS_LARGE_AREA_KM2.
const SPATIAL_ANALYSIS_LARGE_AREA_KM2 =
    Number(process.env.REACT_APP_SPATIAL_ANALYSIS_LARGE_AREA_KM2) || 150;

/**
 * Wrap a longitude into the canonical [-180, 180) range. Leaflet's infinite
 * horizontal scroll lets a polygon drawn on a panned map carry longitudes like
 * 153.23 - 360 = -206.77, which match no server-side raster bounds. Normalize
 * before sending coordinates to the backend so behaviour is independent of pan state.
 */
function wrapLongitude(lon: number): number {
    return ((lon + 180) % 360 + 360) % 360 - 180;
}

/** Study-area size in km² from a polygon's outer ring ([lat,lng]), or null if unmeasurable. */
function spatialPolygonAreaKm2(polygon: Polygon): number | null {
    try {
        const ring = polygon.coordinates?.[0] ?? [];
        const lngLat = ring
            .filter(
                (p): p is [number, number] =>
                    Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]),
            )
            .map(([lat, lng]) => [lng, lat] as [number, number]);
        if (lngLat.length < 3) {
            return null;
        }
        const first = lngLat[0];
        const last = lngLat[lngLat.length - 1];
        const closed = first[0] === last[0] && first[1] === last[1] ? lngLat : [...lngLat, first];
        return turf.area(turf.polygon([closed])) / 1_000_000;
    } catch {
        return null;
    }
}

export default function MapComponent({ children, registerSimulationModalOpen }: MapComponentProps) {
    const { t } = useTranslation('mapCore');
    // Initialize context first
    const context = useContext(AppContext);
    if (!context) {
        throw new Error(t('mapShell.errors.mustBeWithinAppProvider'));
    }

    // Destructure context values
    const {mapSettings, modeSettings, global, setModeSettings} = context;
    const {
      setSimulationName,
      setSimulationId,
      setNumberOfVehicles,
      setNumberOfRides,
      setStatus,
      setSimulationError,
      setSimulationHistory,
      setStepStatuses,
    } = useSimulation();
    const {mode} = modeSettings;
    const {activeLayerId, mapLayers} = global;
    const { isFeatureRuntimeEnabled } = useFeatureFlags();
    const simulationResultsCtx = useSimulationResultsContext();

    // Initialize all state hooks
    const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);
    const [selectedPolygon, setSelectedPolygon] = useState<Polygon | null>(null);
    const [selectedLine, setSelectedLine] = useState<Line | null>(null);
    const [isPointModalOpen, setIsPointModalOpen] = useState(false);
    const [isPolygonModalOpen, setIsPolygonModalOpen] = useState(false);
    const [isLineModalOpen, setIsLineModalOpen] = useState(false);
    const [contextMenu, setContextMenu] = useState<{ x: number, y: number, point: Point } | null>(null);
    const [polygonContextMenu, setPolygonContextMenu] = useState<{ x: number, y: number, polygon: Polygon } | null>(null);
    const [lineContextMenu, setLineContextMenu] = useState<{ x: number, y: number, line: Line } | null>(null);
    const [fixedRouteContextMenu, setFixedRouteContextMenu] = useState<{ x: number, y: number, line: Line, routeId: string, routeName: string } | null>(null);
    const [routeStyleDialogOpen, setRouteStyleDialogOpen] = useState(false);
    const [editingRouteStyleId, setEditingRouteStyleId] = useState<string | null>(null);
    const [isImportTripsModalOpen, setIsImportTripsModalOpen] = useState(false);
    const [isImportGTFSModalOpen, setIsImportGTFSModalOpen] = useState(false);
    const [isProcessingGTFS, setIsProcessingGTFS] = useState(false);
    const [gtfsError, setGtfsError] = useState<string | null>(null);
    const [droppedFile, setDroppedFile] = useState<DroppedFileType | null>(null);
    const [isDropActive, setIsDropActive] = useState(false);
    const [isImportKMLModalOpen, setIsImportKMLModalOpen] = useState(false);
    const [shouldFitBounds, setShouldFitBounds] = useState(false);
    const [fitBoundsScope, setFitBoundsScope] = useState<'lastLayer' | 'allObjects'>('lastLayer');
    const [osmImportProgress, setOsmImportProgress] = useState<number | null>(null);
    const [isOsmLocationsModalOpen, setIsOsmLocationsModalOpen] = useState(false);
    const [osmLocationsLoading, setOsmLocationsLoading] = useState(false);
    const [osmLocationsPolygon, setOsmLocationsPolygon] = useState<Polygon | null>(null);
    const [isIsochroneModalOpen, setIsIsochroneModalOpen] = useState(false);
    const [isochronePoint, setIsochronePoint] = useState<Point | null>(null);
    const [isochroneLoading, setIsochroneLoading] = useState(false);
    const [isochroneError, setIsochroneError] = useState<string | null>(null);
    const [isMeetingPointsModalOpen, setIsMeetingPointsModalOpen] = useState(false);
    const [meetingPointsPolygon, setMeetingPointsPolygon] = useState<Polygon | null>(null);
    const [meetingPointsLoading, setMeetingPointsLoading] = useState(false);
    const [transitScanState, transitScanActions] = useTransitScan();
    
    type RightDrawerKey =
        | 'fleetMenu'
        | 'simulationResults'
        | 'generateRides'
        | 'fixedRoutes'
        | 'dataTransfer'
        | 'attributeTable'
        | 'populationAnalysis'
        | 'spatialAnalysis'
        | 'bugReport';

    // Single source of truth for right-side drawers/tools.
    // Requirement: only one drawer open at a time.
    const [activeRightDrawer, setActiveRightDrawer] = useState<RightDrawerKey | null>(null);

    const toggleRightDrawer = (key: RightDrawerKey) => {
      if (!isPanelRuntimeEnabled(key, isFeatureRuntimeEnabled)) {
        return;
      }
      setActiveRightDrawer(prev => (prev === key ? null : key));
    };

    const openRightDrawer = (key: RightDrawerKey) => {
      if (!isPanelRuntimeEnabled(key, isFeatureRuntimeEnabled)) {
        return;
      }
      setActiveRightDrawer(key);
    };

    const closeRightDrawer = () => {
      setActiveRightDrawer(null);
    };

    // Derived open flags (used by existing child components)
    const isFleetMenuOpen = activeRightDrawer === 'fleetMenu';
    // Fleet vehicle modal and pick mode state
    const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
    const [vehicleDraft, setVehicleDraft] = useState<Vehicle | null>(null);
    const [pickMode, setPickMode] = useState<'start' | 'end' | null>(null);
    const [isStopPickMode, setIsStopPickMode] = useState(false);
    const [isFixedRouteCaptureMode, setIsFixedRouteCaptureMode] = useState(false);
    const [capturePreviewPoints, setCapturePreviewPoints] = useState<[number, number][]>([]);
    const [capturePreviewConsumedCount, setCapturePreviewConsumedCount] = useState(0);
    const [capturePreviewSessionId, setCapturePreviewSessionId] = useState<number | null>(null);
    const isFixedRouteCaptureModeRef = useRef(false);
    const capturePreviewSessionIdRef = useRef<number | null>(null);
    const lastRoutedPreviewSignatureRef = useRef<string | null>(null);
    const activeRoutedLineSignatureRef = useRef<string | null>(null);
    const previewFallbackTimeoutsRef = useRef<number[]>([]);
    const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false);
    const [editFleetTrigger, setEditFleetTrigger] = useState<{ fleetId: string; nonce: number } | null>(null);
    const triggerEditFleet = useCallback((fleetId: string) => {
      setEditFleetTrigger(prev => ({ fleetId, nonce: (prev?.nonce ?? 0) + 1 }));
    }, []);
    const [isOnboardingWizardOpen, setIsOnboardingWizardOpen] = useState(false);
    const [wizardEntrypoint, setWizardEntrypoint] = useState<WizardEntrypoint>('auto_open');
    const [wizardMapInteraction, setWizardMapInteraction] = useState<'idle' | 'wizard-polygon' | 'wizard-hub'>('idle');
    const [wizardMapResumeToken, setWizardMapResumeToken] = useState(0);
    const isSimulationResultsMenuOpen = activeRightDrawer === 'simulationResults';
    const isGenerateRidesDrawerOpen = activeRightDrawer === 'generateRides';
    const isFixedRoutesDrawerOpen = activeRightDrawer === 'fixedRoutes';
    const isDataTransferDrawerOpen = activeRightDrawer === 'dataTransfer';
    const [dataTransferDrawerInitialTab, setDataTransferDrawerInitialTab] = useState<string | undefined>(undefined);
    const isAttributeTableDrawerOpen = activeRightDrawer === 'attributeTable';

    const isPopulationAnalysisDrawerOpen = activeRightDrawer === 'populationAnalysis';
    const isSpatialAnalysisDrawerOpen = activeRightDrawer === 'spatialAnalysis';
    const isBugReportDrawerOpen = activeRightDrawer === 'bugReport';
    // Hosted-network browse state (PRD-04) — shared via NetworkBrowseContext (App level)
    // so the map layers AND the Fixed Routes drawer's Network section share one selection.
    const {
      feedId: networkFeedId,
      version: networkVersion,
      selectedRouteId: networkSelectedRouteId,
      setSelectedRouteId: setNetworkSelectedRouteId,
      selectedStopId: networkSelectedStopId,
      setSelectedStopId: setNetworkSelectedStopId,
      status: networkStatus,
      setStatus: setNetworkStatus,
      selectFeed: selectNetworkFeed,
      zoomRequest: networkZoomRequest,
      focusedStop: networkFocusedStop,
      selectedVariant: networkSelectedVariant,
      pickerOpen: networkPickerOpen,
      setPickerOpen: setNetworkPickerOpen,
    } = useNetworkBrowse();
    const spatialAnalysis = useSpatialAnalysis();
    const [mapReady, setMapReady] = useState(false);

    // Reset data-transfer tab when leaving that drawer.
    useEffect(() => {
      if (activeRightDrawer !== 'dataTransfer') {
        setDataTransferDrawerInitialTab(undefined);
      }
    }, [activeRightDrawer]);

    useEffect(() => {
      updateAgentContract({
        activeDrawer: activeRightDrawer,
        readiness: { mapReady },
        availablePrimaryActions: [
          'open:populationAnalysis',
          'open:generateRides',
          'open:fixedRoutes',
          'open:fleetMenu',
          'open:attributeTable',
          'open:simulationResults',
          'open:dataTransfer',
          'open:bugReport',
        ],
      });
      emitAutomationEvent('map_state_changed', {
        activeDrawer: activeRightDrawer ?? 'none',
        mapReady,
      });
    }, [activeRightDrawer, mapReady]);

    // Run once: normalize legacy trip styles in existing layers.
    const hasRunMissionControlMigrationRef = useRef(false);
    useEffect(() => {
      if (hasRunMissionControlMigrationRef.current) return;
      if (mapLayers.length === 0) return;

      const migration = migrateMissionControlTripStyles(mapLayers);
      if (migration.changed) {
        global.setLayers(migration.layers);
      }
      hasRunMissionControlMigrationRef.current = true;
    }, [mapLayers, global]);
    const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
    const [attributeTableLayerId, setAttributeTableLayerId] = useState(
        mapLayers.length > 0 ? mapLayers[0].layerId : 0
    );
    const [attributeTablePast, setAttributeTablePast] = useState<AttributeTableHistoryEntry[]>([]);
    const [attributeTableFuture, setAttributeTableFuture] = useState<AttributeTableHistoryEntry[]>([]);
    const isApplyingAttributeHistoryRef = useRef(false);

    // Fleet state from context
    const { fleets, setFleets } = global;

    // Layers for modal: type is 'point' if first shape is point, else 'line', default to 'point' if empty
    const layersForModal = global.mapLayers.map((l: any) => ({
        id: l.layerId.toString(),
        name: l.layerName,
        type: (l.shapes.length && l.shapes[0]?.type === 'point') ? 'point' as 'point' : 'line' as 'line'
    }));

    const cloneShapeSnapshot = useCallback((shape: any) => JSON.parse(JSON.stringify(shape)), []);

    const handleAttributeTableShapeUpdate = useCallback(
      (layerId: number, shapeId: string, patch: Record<string, any>) => {
        const targetLayer = global.mapLayers.find((layer) => layer.layerId === layerId);
        if (!targetLayer) return false;

        const shapeIndex = targetLayer.shapes.findIndex((shape: any) => String(shape.id) === String(shapeId));
        if (shapeIndex < 0) return false;

        const currentShape = targetLayer.shapes[shapeIndex] as any;
        const nextShape = { ...currentShape, ...patch };
        const changed = Object.keys(patch).some((key) => currentShape[key] !== nextShape[key]);
        if (!changed) return false;

        const previousShapeSnapshot = cloneShapeSnapshot(currentShape);
        const nextShapeSnapshot = cloneShapeSnapshot(nextShape);

        global.setLayers((prevLayers: Layer[]) =>
          prevLayers.map((layer) => {
            if (layer.layerId !== layerId) return layer;
            const idx = layer.shapes.findIndex((shape: any) => String(shape.id) === String(shapeId));
            if (idx < 0) return layer;
            const newShapes = [...layer.shapes];
            newShapes[idx] = { ...(newShapes[idx] as any), ...patch };
            return {
              ...layer,
              shapes: newShapes,
            };
          })
        );

        if (!isApplyingAttributeHistoryRef.current) {
          setAttributeTablePast((prev) =>
            [
              ...prev,
              {
                layerId,
                shapeId: String(shapeId),
                previousShape: previousShapeSnapshot,
                nextShape: nextShapeSnapshot,
              },
            ].slice(-50)
          );
          setAttributeTableFuture([]);
        }
        return true;
      },
      [global, cloneShapeSnapshot]
    );

    const handleAttributeTableUndo = useCallback(() => {
      setAttributeTablePast((prevPast) => {
        if (prevPast.length === 0) return prevPast;
        const entry = prevPast[prevPast.length - 1];

        isApplyingAttributeHistoryRef.current = true;
        global.setLayers((prevLayers: Layer[]) =>
          prevLayers.map((layer) => {
            if (layer.layerId !== entry.layerId) return layer;
            const idx = layer.shapes.findIndex((shape: any) => String(shape.id) === entry.shapeId);
            if (idx < 0) return layer;
            const newShapes = [...layer.shapes];
            newShapes[idx] = cloneShapeSnapshot(entry.previousShape);
            return {
              ...layer,
              shapes: newShapes,
            };
          })
        );
        isApplyingAttributeHistoryRef.current = false;

        setAttributeTableFuture((prevFuture) => [...prevFuture, entry].slice(-50));
        return prevPast.slice(0, -1);
      });
    }, [global, cloneShapeSnapshot]);

    const handleAttributeTableRedo = useCallback(() => {
      setAttributeTableFuture((prevFuture) => {
        if (prevFuture.length === 0) return prevFuture;
        const entry = prevFuture[prevFuture.length - 1];

        isApplyingAttributeHistoryRef.current = true;
        global.setLayers((prevLayers: Layer[]) =>
          prevLayers.map((layer) => {
            if (layer.layerId !== entry.layerId) return layer;
            const idx = layer.shapes.findIndex((shape: any) => String(shape.id) === entry.shapeId);
            if (idx < 0) return layer;
            const newShapes = [...layer.shapes];
            newShapes[idx] = cloneShapeSnapshot(entry.nextShape);
            return {
              ...layer,
              shapes: newShapes,
            };
          })
        );
        isApplyingAttributeHistoryRef.current = false;

        setAttributeTablePast((prevPast) => [...prevPast, entry].slice(-50));
        return prevFuture.slice(0, -1);
      });
    }, [global, cloneShapeSnapshot]);

    // Initialize refs
    const mapRef = useRef<L.Map | null>(null);

    useEffect(() => {
        if (!registerSimulationModalOpen) {
            return;
        }

        registerSimulationModalOpen(() => setIsSimulationModalOpen(true));
        return () => {
            registerSimulationModalOpen(null);
        };
    }, [registerSimulationModalOpen]);

    // E2E/debug hook: expose the Leaflet map instance once it's ready so non-mock Playwright
    // specs can project lat/lng -> container pixels and drive faithful vector interactions
    // (e.g. dragging fixed-route waypoints, which have no lat/lng event hook). Runs in an effect
    // (not in whenReady) because mapRef.current is not yet committed when whenReady first fires.
    // Stripped from production builds. See docs/how-to/e2e-testing.md (non-mock recipe).
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        if (!mapReady) return;
        (window as unknown as { __atlasMap?: L.Map | null }).__atlasMap = mapRef.current;
        return () => {
            const w = window as unknown as { __atlasMap?: L.Map | null };
            if (w.__atlasMap === mapRef.current) w.__atlasMap = null;
        };
    }, [mapReady]);

    // Initialize custom hooks
    const addPoint = useAddPoint();
    const renderMarkers = useRenderMarkers();
    const addPolygon = useAddPolygon();
    const renderPolygons = useRenderPolygons();
    const renderTempPolygon = useRenderTempPolygon(
        wizardMapInteraction === 'wizard-polygon'
            ? () => {
                setModeSettings((prev) => ({
                    ...prev,
                    mode: 'Select',
                }));
                setWizardMapInteraction('idle');
                setWizardMapResumeToken((prev) => prev + 1);
                setIsOnboardingWizardOpen(true);
            }
            : undefined
    );
    const editLayer = useEditLayer();
    const addLine = useAddLine();
    const renderLines = useRenderLines();
    const renderTempLine = useRenderTempLine();

    // Sync fixed routes to map layers
    useFixedRoutesSync();
    
    // Render fixed route stop markers
    const renderFixedRouteStopMarkers = useRenderFixedRouteStopMarkers();
    
    // Expose utility function for creating routes from KML (for development/testing)
    React.useEffect(() => {
      if (typeof window !== 'undefined') {
        (window as any).createNetflixRoute = async () => {
          const { createNetflixRoute } = await import('../../utils/fixedRoutes/createNetflixRoute');
          const { stops, route } = createNetflixRoute();
          const { useFixedRoutes } = await import('../../context/FixedRoutesContext');
          // Note: This won't work directly as hooks can't be called here
          // Instead, we'll dispatch a custom event that the FixedRoutesContext can listen to
          window.dispatchEvent(new CustomEvent('createRouteFromKML', { 
            detail: { stops, route } 
          }));
          return { stops, route };
        };
      }
    }, []);
    
    // Get selected route from context
    const { selectedRouteId, setSelectedRouteId, routes, stops, updateRoute, setRouteGenerationStatus, distanceUnit, editingRouteId, enterEditMode, exitEditMode, bulkAddStopsRoutes } = useFixedRoutes();
    const activeCaptureRouteId = editingRouteId ?? selectedRouteId ?? null;

    // "Edit" from a hosted-network route popup: fork the live route into an editable copy,
    // select it, and open the Fixed Routes drawer (which auto-switches to the Routes tab).
    const forkNetworkRoute = useForkNetworkRoute();
    const handleEditNetworkRoute = useCallback(async (gtfsRouteId: string) => {
        if (!networkFeedId) return;
        try {
            const res = await forkNetworkRoute(networkFeedId, networkVersion ?? null, gtfsRouteId);
            if (!res) return;
            setSelectedRouteId(res.route.id);
            openRightDrawer('fixedRoutes');
        } catch (err) {
            console.error('[map] failed to fork network route for editing', gtfsRouteId, err);
        }
    }, [networkFeedId, networkVersion, forkNetworkRoute, setSelectedRouteId, openRightDrawer]);

    // Auto-load the user's default transit network on sign-in (resolved user -> customer ->
    // none by HTTM). Applies on each fresh app-load (when no network is on the map yet) so it
    // sets the network on entry; it never overrides a network the user has already selected,
    // and the once-per-mount ref avoids a duplicate fetch. `networkFeedId` lives in the
    // app-level provider, so navigating away/back to the map won't re-trigger or yank it.
    const authState = useAuth();
    const defaultNetworkAppliedRef = useRef(false);
    useEffect(() => {
        const uid = authState.user?.userId;
        if (!authState.isAuthenticated || !uid || defaultNetworkAppliedRef.current) return;
        defaultNetworkAppliedRef.current = true;
        if (networkFeedId) return; // a network is already on the map — don't override it
        let active = true;
        getNetworkPreference()
            .then((pref) => {
                if (!active) return;
                if (pref.default_network_feed_id) {
                    selectNetworkFeed(pref.default_network_feed_id);
                }
            })
            .catch(() => undefined);
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState.isAuthenticated, authState.user?.userId]);

    // Compute trip layers that are render-capped (stored data exceeds TRIP_RENDER_CAP and renderAll not set).
    // All trip data is always preserved in state and sent to simulations — the cap is render-only.
    const cappedTripLayers = useMemo(() =>
        global.mapLayers.filter(layer => {
            if (layer.hidden || layer.renderAll) return false;
            const tripCount = layer.shapes.filter(s => isTripLineShape(s)).length;
            return tripCount > TRIP_RENDER_CAP;
        }),
        [global.mapLayers]
    );

    const handleRenderAllTrips = useCallback((layerId: number) => {
        global.setLayers((prev: Layer[]) =>
            prev.map(l => l.layerId === layerId ? { ...l, renderAll: true } : l)
        );
    }, [global.setLayers]);
    
    // Handle fixed route line clicks
    useEffect(() => {
        const handleFixedRouteClick = (e: CustomEvent<{ routeId: string; routeName: string }>) => {
            setSelectedRouteId(e.detail.routeId);
            openRightDrawer('fixedRoutes');
        };
        
        window.addEventListener('fixedRouteLineClick', handleFixedRouteClick as EventListener);
        return () => {
            window.removeEventListener('fixedRouteLineClick', handleFixedRouteClick as EventListener);
        };
    }, [setSelectedRouteId, openRightDrawer]);

    // Handle global GTFS file drops - open drawer and let FixedRoutesContent handle the import
    useEffect(() => {
        const handleGlobalGTFSDrop = (e: CustomEvent<{ file: File }>) => {
            // Open the Fixed Routes drawer (and close any other open drawer)
            openRightDrawer('fixedRoutes');
        };
        
        window.addEventListener('gtfsFileDropped', handleGlobalGTFSDrop as EventListener);
        return () => {
            window.removeEventListener('gtfsFileDropped', handleGlobalGTFSDrop as EventListener);
        };
    }, [openRightDrawer]);

    // Add global drag handlers to prevent Leaflet from interfering
    useEffect(() => {
        const handleDragOver = (e: DragEvent) => {
            // Only prevent default if it's a file drag
            if (e.dataTransfer && e.dataTransfer.types.includes('Files')) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleDrop = (e: DragEvent) => {
            // Only prevent default if it's a file drop
            if (e.dataTransfer && e.dataTransfer.files.length > 0) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('dragover', handleDragOver, false);
        document.addEventListener('drop', handleDrop, false);

        return () => {
            document.removeEventListener('dragover', handleDragOver, false);
            document.removeEventListener('drop', handleDrop, false);
        };
    }, []);

    // All points for dropdowns
    const allPoints = global.mapLayers.flatMap(layer =>
        layer.shapes.filter(s => s.type === 'point').map(p => ({ id: p.id, name: p.name || p.id }))
    );

    // Replace the useDropzone hook with:
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles: File[]) => {
            console.log('Map dropzone onDrop called with:', acceptedFiles);
            onDropMapFile(
                acceptedFiles,
                global,
                setImportedFile,
                setImportModalOpen,
                handleImportedShapes,
                setIsDropActive
            );
        },
        onDragOver: (e) => {
            e.preventDefault();
            e.stopPropagation();
        },
        onDragEnter: (e) => {
            e.preventDefault();
            e.stopPropagation();
        },
        noClick: true,
        noKeyboard: true,
        // Explicitly accept all file types to ensure drops work
        accept: undefined, // Accept all files
        // Don't disable the dropzone
        disabled: false,
    });

    // Use extracted handlers
    const keyboardHandler = useKeyboardHandlers(
        modeSettings.mode,
        modeSettings.drawingPolygonPoints,
        modeSettings.drawingLinePoints,
        setModeSettings,
        global,
        addPolygon
    );
    const mapClickHandler = useMapClickHandler({
        mode,
        modeSettings,
        setModeSettings,
        addPoint,
        addPolygon,
        addLine,
        setSelectedShapes: global.setSelectedShapes,
        global,
        getPointDraft:
            wizardMapInteraction === 'wizard-hub'
                ? (coordinates) => ({
                    name: getNextWizardHubName(),
                    coordinates,
                    creationSource: 'quick-start-hub',
                })
                : undefined,
        onPolygonCompleted:
            wizardMapInteraction === 'wizard-polygon'
                ? () => {
                    setModeSettings((prev) => ({
                        ...prev,
                        mode: 'Select',
                    }));
                    setWizardMapInteraction('idle');
                    setWizardMapResumeToken((prev) => prev + 1);
                    setIsOnboardingWizardOpen(true);
                }
                : undefined,
    });

    const {
        handlePointContextMenu,
        handlePolygonContextMenu,
        handleLineContextMenu,
        handleDeleteShape
    } = useContextMenuHandlers({
        editLayer,
        activeLayerId,
        mapLayers: global.mapLayers
    });

    const {
        processGTFSFile: handleProcessGTFSFile
    } = useDragDropHandlers({
        setIsProcessingGTFS,
        setGtfsError,
        setDroppedFile,
        setIsImportTripsModalOpen,
        setIsDropActive,
        handleImportTrips,
        handleImportKMLShapes,
        global,
        editLayer,
        setShouldFitBounds
    });

    // Use the consolidated map event handlers
    const mapEventHandlers = useMapEventHandlers({
        mapRef,
        global,
        setOsmImportProgress
    });

    // Add event listeners for map events
    useEffect(() => {
        const zoomToLayerHandler = (e: Event) => mapEventHandlers.handleZoomToLayer(e as CustomEvent<{ shapes: Shape[] }>);
        const zoomToShapeHandler = (e: Event) => mapEventHandlers.handleZoomToShape(e as CustomEvent<{ shape: Shape }>);

        window.addEventListener('zoomToLayer', zoomToLayerHandler);
        window.addEventListener('zoomToShape', zoomToShapeHandler);
        
        return () => {
            window.removeEventListener('zoomToLayer', zoomToLayerHandler);
            window.removeEventListener('zoomToShape', zoomToShapeHandler);
        };
    }, [mapEventHandlers]);

    // Add keyboard event listener
    useEffect(() => {
        window.addEventListener('keydown', keyboardHandler);
        return () => {
            window.removeEventListener('keydown', keyboardHandler);
        };
    }, [keyboardHandler]);

    // Shortcut: press "f" to open the Fixed Routes drawer.
    useEffect(() => {
        const handleFixedRoutesShortcut = (event: KeyboardEvent) => {
            if (event.defaultPrevented || event.repeat) return;
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (!event.key || event.key.toLowerCase() !== 'f') return;

            const target = event.target as HTMLElement | null;
            if (target) {
                const tagName = target.tagName;
                const isFormField = tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
                if (isFormField || target.isContentEditable) {
                    return;
                }
            }

            event.preventDefault();
            openRightDrawer('fixedRoutes');
        };

        window.addEventListener('keydown', handleFixedRoutesShortcut);
        return () => {
            window.removeEventListener('keydown', handleFixedRoutesShortcut);
        };
    }, [openRightDrawer]);

    // Listen for custom event to open Data Transfer Drawer with specific tab/mode
    useEffect(() => {
        const handleOpenDataTransferDrawer = (event: CustomEvent<{ dataType?: string; mode?: 'download' | 'upload' }>) => {
            const { dataType } = event.detail || {};
            if (dataType) {
                setDataTransferDrawerInitialTab(dataType);
            }
            openRightDrawer('dataTransfer');
        };

        window.addEventListener('openDataTransferDrawer', handleOpenDataTransferDrawer as EventListener);
        return () => {
            window.removeEventListener('openDataTransferDrawer', handleOpenDataTransferDrawer as EventListener);
        };
    }, [openRightDrawer]);

    useEffect(() => {
        const handleFixedRouteImportedFromResults = () => {
            openRightDrawer('fixedRoutes');
        };

        window.addEventListener('fixedRouteImportedFromResults', handleFixedRouteImportedFromResults as EventListener);
        return () => {
            window.removeEventListener('fixedRouteImportedFromResults', handleFixedRouteImportedFromResults as EventListener);
        };
    }, [openRightDrawer]);

    // Wrapper for map click handler that checks for fixed-route capture/stop pick mode
    const wrappedMapClickHandler = useCallback((e: { latlng: { lat: number; lng: number }, originalEvent: { defaultPrevented: boolean } }) => {
        // Handle fixed route capture first
        if (isFixedRouteCaptureMode && modeSettings.mode !== 'Draw Polygon' && !e.originalEvent.defaultPrevented) {
            const { lat, lng } = e.latlng;
            window.dispatchEvent(new CustomEvent('fixedRouteCaptureLeftClick', {
                detail: { lat, lng }
            }));
            return;
        }

        // Handle stop pick mode first
        if (isStopPickMode && !e.originalEvent.defaultPrevented) {
            const { lat, lng } = e.latlng;
            window.dispatchEvent(new CustomEvent('pickStopLocation', {
                detail: { lat, lng }
            }));
            setIsStopPickMode(false);
            return;
        }
        
        // Otherwise, use the normal map click handler
        mapClickHandler(e);
    }, [isFixedRouteCaptureMode, isStopPickMode, mapClickHandler, modeSettings.mode]);

    const wrappedMapContextMenuHandler = useCallback((_e: { latlng: { lat: number; lng: number }, originalEvent: { preventDefault: () => void; stopPropagation?: () => void } }) => {
        // Waypoint capture is disabled; preserve default context-menu behavior.
    }, []);
    
    // MapEvents component using extracted handler
    const MapEventsWrapper = () => (
        <MapEvents
            onMapClick={wrappedMapClickHandler}
            onMapContextMenu={wrappedMapContextMenuHandler}
            polygonDrawPreview={{
                enabled: modeSettings.mode === 'Draw Polygon',
                points: modeSettings.drawingPolygonPoints,
                color: liftangoBrandTokens.missionControl.zonePreviewBorder,
            }}
        />
    );

    // Add context menu event listeners with proper type casting
    useEffect(() => {
        const handlePointContextMenuEvent = (event: Event) => {
            const result = handlePointContextMenu(event as CustomEvent);
            setContextMenu(result);
        };

        const handlePolygonContextMenuEvent = (event: Event) => {
            const result = handlePolygonContextMenu(event as CustomEvent);
            setPolygonContextMenu(result);
        };

        const handleLineContextMenuEvent = (event: Event) => {
            const result = handleLineContextMenu(event as CustomEvent);
            setLineContextMenu(result);
        };

        const handleFixedRouteContextMenuEvent = (event: Event) => {
            const customEvent = event as CustomEvent<{ x: number, y: number, line: Line, routeId: string, routeName: string }>;
            if (customEvent.detail) {
                setFixedRouteContextMenu({
                    x: customEvent.detail.x,
                    y: customEvent.detail.y,
                    line: customEvent.detail.line,
                    routeId: customEvent.detail.routeId,
                    routeName: customEvent.detail.routeName,
                });
            }
        };

        window.addEventListener('pointContextMenu', handlePointContextMenuEvent);
        window.addEventListener('polygonContextMenu', handlePolygonContextMenuEvent);
        window.addEventListener('lineContextMenu', handleLineContextMenuEvent);
        window.addEventListener('fixedRouteContextMenu', handleFixedRouteContextMenuEvent);
        
        return () => {
            window.removeEventListener('pointContextMenu', handlePointContextMenuEvent);
            window.removeEventListener('polygonContextMenu', handlePolygonContextMenuEvent);
            window.removeEventListener('lineContextMenu', handleLineContextMenuEvent);
            window.removeEventListener('fixedRouteContextMenu', handleFixedRouteContextMenuEvent);
        };
    }, [handlePointContextMenu, handlePolygonContextMenu, handleLineContextMenu]);

    // Render instruction box when in drawing mode
    const renderDrawingInstructions = () => {
        if (!mode) return null;
        
        return (
            <DrawingInstructions
                mode={mode}
                polygonDraftVertexCount={modeSettings.drawingPolygonPoints.length}
                lineDraftVertexCount={modeSettings.drawingLinePoints.length}
            />
        );
    };

    // Update the GTFS file input handler
    const openGTFSImport = () => handleGTFSImport({
        global,
        editLayer,
        setShouldFitBounds,
        setIsProcessingGTFS,
        setGtfsError,
        setIsImportGTFSModalOpen
    });

    // Add event listeners for import actions
    useEffect(() => {
        const handleExcelImport = () => {
            console.log('Excel import clicked');
            setIsImportTripsModalOpen(true);
        };
        
        const handleGTFSImport = () => {
            console.log('GTFS import clicked');
            openGTFSImport();
        };
        
        const handleKMLImport = () => {
            console.log('KML import clicked');
            setIsImportKMLModalOpen(true);
        };

        window.addEventListener('openExcelImport', handleExcelImport);
        window.addEventListener('openGTFSImport', handleGTFSImport);
        window.addEventListener('openKMLImport', handleKMLImport);

        return () => {
            window.removeEventListener('openExcelImport', handleExcelImport);
            window.removeEventListener('openGTFSImport', handleGTFSImport);
            window.removeEventListener('openKMLImport', handleKMLImport);
        };
    }, [openGTFSImport]);

    // Allow other UI (e.g. Map Gallery) to request recentering to all objects.
    useEffect(() => {
        const handleFitMapToObjects = () => {
            setFitBoundsScope('allObjects');
            setShouldFitBounds(true);
        };
        window.addEventListener('fitMapToObjects', handleFitMapToObjects as EventListener);
        return () => {
            window.removeEventListener('fitMapToObjects', handleFitMapToObjects as EventListener);
        };
    }, []);

    // Fit map to bounds after import if requested
    useEffect(() => {
        if (shouldFitBounds && mapRef.current) {
            const coords: [number, number][] = [];

            const pushLayerCoords = (layer: Layer) => {
                if (layer.hidden) return;
                layer.shapes.forEach((shape) => {
                    // Skip hidden shapes when present (legacy shapes may not have hidden)
                    if ((shape as any)?.hidden) return;
                    if (shape.type === 'point') {
                        coords.push(shape.coordinates);
                    } else if (shape.type === 'line') {
                        coords.push(...shape.coordinates);
                    } else if (shape.type === 'polygon') {
                        // Polygon coordinates are rings: [ [lat,lng]... ][]
                        shape.coordinates.forEach((ring) => coords.push(...ring));
                    }
                });
            };

            if (fitBoundsScope === 'allObjects') {
                global.mapLayers.forEach(pushLayerCoords);
                // Include fixed-route stops (even if they aren't synced to a layer yet)
                (stops ?? []).forEach((s) => {
                    const lat = s?.coordinates?.lat;
                    const lng = s?.coordinates?.lng;
                    if (typeof lat === 'number' && typeof lng === 'number') {
                        coords.push([lat, lng]);
                    }
                });
            } else {
                // Find the most recently added layer (last in the array)
                const lastLayer = global.mapLayers[global.mapLayers.length - 1];
                if (lastLayer) pushLayerCoords(lastLayer);
            }

            if (coords.length > 0) {
                const bounds = L.latLngBounds(coords.map(([lat, lng]) => [lat, lng]));
                mapRef.current.fitBounds(bounds.pad(0.2));
            }
            setShouldFitBounds(false);
            setFitBoundsScope('lastLayer');
        }
    }, [shouldFitBounds, fitBoundsScope, global.mapLayers, stops, setShouldFitBounds]);

    // Add state for import modal and imported file
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importedFile, setImportedFile] = useState<File | null>(null);

    // Cursor style by interaction mode (polygon draw gets its own visual indicator).
    const isPolygonDrawMode = modeSettings.mode === 'Draw Polygon' || wizardMapInteraction === 'wizard-polygon';
    const mapCursor = (isPolygonDrawMode || pickMode || isStopPickMode || isFixedRouteCaptureMode || wizardMapInteraction === 'wizard-hub')
        ? 'crosshair'
        : 'auto';
    
    // Handle stop location pick mode
    useEffect(() => {
        const handleStartPickStopLocation = () => {
            setIsStopPickMode(true);
        };
        
        const handleCancelPickStopLocation = () => {
            setIsStopPickMode(false);
        };
        
        window.addEventListener('startPickStopLocation', handleStartPickStopLocation);
        window.addEventListener('cancelPickStopLocation', handleCancelPickStopLocation);
        
        return () => {
            window.removeEventListener('startPickStopLocation', handleStartPickStopLocation);
            window.removeEventListener('cancelPickStopLocation', handleCancelPickStopLocation);
        };
    }, []);

    // Handle fixed-route capture mode events from RouteBuilder
    useEffect(() => {
        isFixedRouteCaptureModeRef.current = isFixedRouteCaptureMode;
    }, [isFixedRouteCaptureMode]);

    useEffect(() => {
        capturePreviewSessionIdRef.current = capturePreviewSessionId;
    }, [capturePreviewSessionId]);

    useEffect(() => {
        const handleStartFixedRouteCapture = (event: Event) => {
            const customEvent = event as CustomEvent<{ sessionId?: number }>;
            setCapturePreviewPoints([]);
            setCapturePreviewConsumedCount(0);
            const nextSessionId = customEvent.detail?.sessionId ?? null;
            capturePreviewSessionIdRef.current = nextSessionId;
            setCapturePreviewSessionId(nextSessionId);
            lastRoutedPreviewSignatureRef.current = activeRoutedLineSignatureRef.current;
            isFixedRouteCaptureModeRef.current = true;
            setIsFixedRouteCaptureMode(true);
        };
        
        const handleCancelFixedRouteCapture = () => {
            setCapturePreviewPoints([]);
            setCapturePreviewConsumedCount(0);
            previewFallbackTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
            previewFallbackTimeoutsRef.current = [];
            capturePreviewSessionIdRef.current = null;
            setCapturePreviewSessionId(null);
            isFixedRouteCaptureModeRef.current = false;
            setIsFixedRouteCaptureMode(false);
        };
        
        const handleFinishFixedRouteCapture = () => {
            setCapturePreviewPoints([]);
            setCapturePreviewConsumedCount(0);
            previewFallbackTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
            previewFallbackTimeoutsRef.current = [];
            capturePreviewSessionIdRef.current = null;
            setCapturePreviewSessionId(null);
            isFixedRouteCaptureModeRef.current = false;
            setIsFixedRouteCaptureMode(false);
        };

        const handleCaptureSegmentRendered = (event: Event) => {
            // Intentionally a no-op. The dotted capture-preview leg must stay on the map
            // until the REAL routed line for that leg actually returns — it should never
            // disappear before the route line replaces it. Consumption is therefore driven
            // solely by the routed-line signature changing (see the activeRoutedLineSignature
            // effect below), i.e. the dotted line is replaced only when the real route line
            // arrives. (Previously a 350ms timer trimmed the preview even if the route had
            // not returned yet, which made the dotted line blink out into empty space.)
            void event;
        };

        const handleCaptureStopQueued = (event: Event) => {
            if (!isFixedRouteCaptureModeRef.current) return;
            const customEvent = event as CustomEvent<{ sessionId?: number; lat: number; lng: number }>;
            const eventSessionId = customEvent.detail?.sessionId;
            const activeSessionId = capturePreviewSessionIdRef.current;
            if (activeSessionId != null && eventSessionId == null) {
                return;
            }
            if (activeSessionId != null && eventSessionId != null && eventSessionId !== activeSessionId) {
                return;
            }
            const lat = customEvent.detail?.lat;
            const lng = customEvent.detail?.lng;
            if (typeof lat !== 'number' || typeof lng !== 'number') return;
            setCapturePreviewPoints((prev) => {
                const next = [...prev, [lat, lng] as [number, number]];
                // Ensure one pending dotted leg remains visible after each accepted click.
                setCapturePreviewConsumedCount((consumed) =>
                    Math.min(consumed, Math.max(0, next.length - 2))
                );
                return next;
            });
        };
        
        window.addEventListener('startFixedRouteCapture', handleStartFixedRouteCapture as EventListener);
        window.addEventListener('cancelFixedRouteCapture', handleCancelFixedRouteCapture);
        window.addEventListener('finishFixedRouteCapture', handleFinishFixedRouteCapture);
        window.addEventListener('fixedRouteCaptureSegmentRendered', handleCaptureSegmentRendered as EventListener);
        window.addEventListener('fixedRouteCaptureStopQueued', handleCaptureStopQueued as EventListener);
        
        return () => {
            previewFallbackTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
            previewFallbackTimeoutsRef.current = [];
            window.removeEventListener('startFixedRouteCapture', handleStartFixedRouteCapture as EventListener);
            window.removeEventListener('cancelFixedRouteCapture', handleCancelFixedRouteCapture);
            window.removeEventListener('finishFixedRouteCapture', handleFinishFixedRouteCapture);
            window.removeEventListener('fixedRouteCaptureSegmentRendered', handleCaptureSegmentRendered as EventListener);
            window.removeEventListener('fixedRouteCaptureStopQueued', handleCaptureStopQueued as EventListener);
        };
    }, []);

    useEffect(() => {
        if (!isFixedRoutesDrawerOpen) {
            setCapturePreviewPoints([]);
            setCapturePreviewConsumedCount(0);
            previewFallbackTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
            previewFallbackTimeoutsRef.current = [];
            capturePreviewSessionIdRef.current = null;
            setCapturePreviewSessionId(null);
            isFixedRouteCaptureModeRef.current = false;
            lastRoutedPreviewSignatureRef.current = null;
        }
    }, [isFixedRoutesDrawerOpen]);

    const activeRoutedLineSignature = useMemo(() => {
        if (!activeCaptureRouteId) return null;
        const fixedRoutesLayer = global.mapLayers.find((layer) => layer.layerName === 'Fixed Routes');
        if (!fixedRoutesLayer) return null;
        const activeLine = fixedRoutesLayer.shapes.find((shape) => {
            if (shape.type !== 'line') return false;
            const metadata = (shape as any).fixedRouteMetadata;
            return metadata?.fixedRouteId === activeCaptureRouteId;
        }) as (Line & { coordinates: [number, number][] }) | undefined;
        if (!activeLine?.coordinates || activeLine.coordinates.length < 2) return null;
        const coords = activeLine.coordinates;
        const len = coords.length;
        const tail = coords
            .slice(Math.max(0, len - 3))
            .map(([lat, lng]) => `${lat.toFixed(5)},${lng.toFixed(5)}`)
            .join('|');
        return `${activeCaptureRouteId}:${len}:${tail}`;
    }, [global.mapLayers, activeCaptureRouteId]);

    useEffect(() => {
        activeRoutedLineSignatureRef.current = activeRoutedLineSignature;
        if (!isFixedRouteCaptureMode) {
            lastRoutedPreviewSignatureRef.current = activeRoutedLineSignature;
            return;
        }
        if (!activeRoutedLineSignature) return;

        const previousSignature = lastRoutedPreviewSignatureRef.current;
        if (previousSignature !== activeRoutedLineSignature) {
            // The routed line for the captured route just appeared or grew, so the real geometry now
            // covers another captured leg — consume one dotted preview leg (the solid line replaces it).
            // The FIRST appearance (previousSignature == null) counts too: for a brand-new route the
            // routed line going from absent -> present IS the first leg arriving and must consume the
            // dotted leg. Skipping it (the old behavior) left a permanent stale dotted line — most
            // visibly on a 2-stop route, whose single leg only ever produces this one transition.
            setCapturePreviewConsumedCount((prev) => prev + 1);
            lastRoutedPreviewSignatureRef.current = activeRoutedLineSignature;
        }
    }, [isFixedRouteCaptureMode, activeRoutedLineSignature]);

    // Robust backstop for clearing the dotted capture preview: once the captured route's geometry is
    // fully up to date with its committed stops (stored stopsHash matches a freshly computed one, i.e.
    // no recompute is pending), the solid route line completely represents the route — so no dotted
    // preview leg should remain. This guarantees the dotted line disappears when the route line
    // appears, independent of the incremental consume counter above (which can lag when several legs
    // land in a single geometry update). We advance the consumed count to leave just the last point as
    // an anchor (no visible leg) rather than wiping points, so the NEXT click still shows a fresh leg.
    useEffect(() => {
        if (!isFixedRouteCaptureMode || !activeCaptureRouteId) return;
        if (capturePreviewPoints.length < 2) return;
        const route = routes.find((r) => r.id === activeCaptureRouteId);
        if (!route || !route.geometry?.coordinates?.length || !route.geometry.stopsHash) return;
        const routeStops = route.stops
            .map((sc) => stops.find((s) => s.id === sc.stopId))
            .filter((s): s is FixedRouteStop => !!s && !!s.coordinates);
        if (routeStops.length !== route.stops.length) return; // a stop hasn't resolved yet — wait
        if (route.geometry.stopsHash !== computeRouteGeometryHash(route, routeStops)) return; // still routing
        setCapturePreviewConsumedCount((prev) => Math.max(prev, capturePreviewPoints.length - 1));
    }, [isFixedRouteCaptureMode, activeCaptureRouteId, routes, stops, capturePreviewPoints.length]);

    const visibleCapturePreviewPoints = useMemo(() => {
        const maxStart = Math.max(0, capturePreviewPoints.length - 1);
        const start = Math.min(capturePreviewConsumedCount, maxStart);
        return capturePreviewPoints.slice(start);
    }, [capturePreviewPoints, capturePreviewConsumedCount]);
    
    // Keep cursor behavior simple and consistent across tool modes.
    useEffect(() => {
        if (mapRef.current) {
            const mapContainer = mapRef.current.getContainer();
            if (mapContainer) {
                mapContainer.classList.remove('polygon-draw-cursor');
                mapContainer.style.setProperty('cursor', mapCursor, 'important');
            }
        }
    }, [mapCursor]);

    // Wrapper for OSM Locations Import to provide correct arguments
    const handleOsmLocationsImport = ({ selectedTypes, polygon }: { selectedTypes: string[], polygon: Polygon }) => {
        return rawHandleOsmLocationsImport({
            selectedTypes,
            polygon,
            global,
            setOsmLocationsLoading,
            setIsOsmLocationsModalOpen,
        });
    };

    const [selectedAnalysisPolygonId, setSelectedAnalysisPolygonId] = useState<string | null>(null);

    // Get all polygons from all layers, with layer name for display
    const allPolygons: Array<Polygon & { _layerName: string }> = mapLayers.flatMap(layer =>
      layer.shapes
        .filter((s): s is Polygon => s.type === 'polygon')
        .map(polygon => ({
          ...polygon,
          _layerName: layer.layerName
        }))
    );

    // --- Population Data State ---
    const [populationModalOpen, setPopulationModalOpen] = useState(false);
    const [populationTotal, setPopulationTotal] = useState<number | null>(null);
    const [populationLoading, setPopulationLoading] = useState(false);
    const [populationError, setPopulationError] = useState<string | null>(null);
    const [populationWarning, setPopulationWarning] = useState<string | null>(null);
    const [transitLoading, setTransitLoading] = useState(false);
    const [transitError, setTransitError] = useState<string | null>(null);
    const [coveredPopulation, setCoveredPopulation] = useState<number | null>(null);
    const [uncoveredPopulation, setUncoveredPopulation] = useState<number | null>(null);
    const [batchResults, setBatchResults] = useState<any[] | null>(null);
    const [basemapLoadError, setBasemapLoadError] = useState<string | null>(null);
    const basemapTileErrorCountRef = useRef(0);

    useEffect(() => {
      basemapTileErrorCountRef.current = 0;
      setBasemapLoadError(null);
    }, [mapSettings.basemap]);

    const googleBasemap = useGoogleBasemap();
    const failedGoogleMapTypesRef = useRef(new Set<GoogleMapType>());
    const pendingGoogleFallbackMessageRef = useRef<string | null>(null);
    const { basemap: currentBasemap, setBasemap } = mapSettings;

    useEffect(() => {
      if (googleBasemap.active && !(GOOGLE_BASEMAP_KEYS as readonly string[]).includes(currentBasemap)) {
        setBasemap('standard');
      }
      if (pendingGoogleFallbackMessageRef.current) {
        setBasemapLoadError(pendingGoogleFallbackMessageRef.current);
        pendingGoogleFallbackMessageRef.current = null;
      }
    }, [googleBasemap.active, currentBasemap, setBasemap]);

    const handleGoogleBasemapUnavailable = useCallback((failed: GoogleMapType) => {
      failedGoogleMapTypesRef.current.add(failed);
      const fallback = alternateGoogleMapType(failed);
      if (failedGoogleMapTypesRef.current.has(fallback)) {
        setBasemapLoadError(t('mapShell.googleBasemapUnavailable'));
        return;
      }
      pendingGoogleFallbackMessageRef.current = t('mapShell.googleBasemapSwitched', { name: failed, fallback });
      setBasemap(basemapForGoogleMapType(fallback));
    }, [setBasemap, t]);

    const [isCreatingSpatialAnalysisTicket, setIsCreatingSpatialAnalysisTicket] = useState(false);
    const [spatialAnalysisTicketNotice, setSpatialAnalysisTicketNotice] = useState<{
      severity: 'success' | 'error';
      message: string;
    } | null>(null);

    const handleCreateSpatialAnalysisTicket = useCallback(async (polygon: Polygon) => {
      if (isCreatingSpatialAnalysisTicket) {
        return;
      }

      const sourceLayer = mapLayers.find((layer) =>
        layer.shapes.some((shape) => shape.type === 'polygon' && shape.id === polygon.id)
      );

      setIsCreatingSpatialAnalysisTicket(true);
      setSpatialAnalysisTicketNotice(null);

      try {
        const result = await createSpatialAnalysisTicket({
          polygon,
          layerName: sourceLayer?.layerName,
          currentUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          submitter: context.userData.userDetails,
        });
        setSpatialAnalysisTicketNotice({
          severity: 'success',
          message: t('mapShell.spatialAnalysisTicketCreated', { key: result.ticket_key }),
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : t('mapShell.spatialAnalysisTicketFailed');
        setSpatialAnalysisTicketNotice({
          severity: 'error',
          message,
        });
      } finally {
        setIsCreatingSpatialAnalysisTicket(false);
      }
    }, [isCreatingSpatialAnalysisTicket, mapLayers, t]);

    // Start a hosted (fixture-backed) spatial-analysis run for the polygon and
    // open the drawer. Run state + polling live in SpatialAnalysisContext, so
    // the run survives the drawer being closed.
    // Pending large-polygon run awaiting user confirmation (null = none).
    const [largePolygonConfirm, setLargePolygonConfirm] = useState<{ polygon: Polygon; areaKm2: number } | null>(null);

    const startSpatialAnalysisRun = useCallback(async (polygon: Polygon) => {
      const sourceLayer = mapLayers.find((layer) =>
        layer.shapes.some((shape) => shape.type === 'polygon' && shape.id === polygon.id)
      );
      openRightDrawer('spatialAnalysis');
      await spatialAnalysis.startRun({
        source: {
          polygonId: polygon.id,
          polygonName: polygon.name,
          layerId: sourceLayer?.layerId,
          layerName: sourceLayer?.layerName,
          mapUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        // Normalize wrapped longitudes ([lat, lng] convention) so a polygon drawn
        // on a panned map still resolves the right region server-side.
        polygonCoordinates: polygon.coordinates.map(ring =>
          ring.map(([lat, lng]) => [lat, wrapLongitude(lng)] as [number, number])
        ),
      });
    }, [mapLayers, spatialAnalysis, openRightDrawer]);

    const handleRunSpatialAnalysis = useCallback(async (polygon: Polygon) => {
      // Confirm before starting a slow/heavy run on a large study area.
      const areaKm2 = spatialPolygonAreaKm2(polygon);
      if (areaKm2 !== null && areaKm2 > SPATIAL_ANALYSIS_LARGE_AREA_KM2) {
        setLargePolygonConfirm({ polygon, areaKm2 });
        return;
      }
      await startSpatialAnalysisRun(polygon);
    }, [startSpatialAnalysisRun]);

    const confirmLargePolygonRun = useCallback(async () => {
      const pending = largePolygonConfirm;
      setLargePolygonConfirm(null);
      if (pending) {
        await startSpatialAnalysisRun(pending.polygon);
      }
    }, [largePolygonConfirm, startSpatialAnalysisRun]);

    // Latest-ref for setLayers so the hydration effect can depend ONLY on the
    // run (not on the unstable `global` object, which is recreated every App
    // render and would otherwise spin this setState-in-effect into a loop).
    const setLayersRef = useRef(global.setLayers);
    setLayersRef.current = global.setLayers;
    // Run id whose GTFS feed has been imported into the Fixed Routes drawer; when
    // set, the run's own (OSM/GTFS-stop) transit layer is suppressed so the Fixed
    // Routes rendering supersedes it.
    const gtfsLoadedRunRef = useRef<string | null>(null);
    const [gtfsLoadedRunId, setGtfsLoadedRunId] = useState<string | null>(null);
    // Latest run id, tracked every render. The GTFS import fires mid-run (as soon
    // as the feed is available) while polling is still active, so it cannot use a
    // per-effect `cancelled` flag (that would abort on the next poll). It checks
    // this ref instead, so only a genuinely different/cleared run aborts an
    // in-flight import.
    const latestRunIdRef = useRef<string | undefined>(undefined);
    latestRunIdRef.current = spatialAnalysis.run?.id;
    // True while the GTFS feed import (fetch -> convert -> bulkAdd) is in flight,
    // so the rail + drawer can signal "still loading transit routes". The import
    // now starts as soon as the feed is available (mid-run), so this can be true
    // while the run is still drafting its report.
    const [gtfsImporting, setGtfsImporting] = useState(false);

    // Reconcile the run's generated layers into mapLayers as it progresses.
    // All generated sets are merged into ONE Atlas layer with a stable
    // id, so each poll upserts that single layer rather than duplicating; the
    // drawer's logical run.layers list is left untouched. Clearing/cancelling
    // the run removes the spatial-analysis layer. The user's hidden toggle on
    // the merged layer is preserved across polls (keyed on the single id).
    useEffect(() => {
      const run = spatialAnalysis.run;
      setLayersRef.current((prevLayers: Layer[]) => {
        const nonSpatial = prevLayers.filter((layer) => !isSpatialAnalysisLayerId(layer.layerId));
        if (!run || run.status === 'cancelled' || run.layers.length === 0) {
          // Cleanup (or nothing to show): only change state if there were spatial layers.
          return nonSpatial.length === prevLayers.length ? prevLayers : nonSpatial;
        }
        // Preserve the user's hidden toggle for the single merged layer.
        const prevHiddenById = new Map(
          prevLayers
            .filter((layer) => isSpatialAnalysisLayerId(layer.layerId))
            .map((layer) => [layer.layerId, layer.hidden] as const),
        );
        const suppressTransit = gtfsLoadedRunId === run.id;
        const spatialLayers = convertSpatialLayersToMapLayers(run.layers).map((layer) => {
          // When GTFS routes are loaded into the Fixed Routes drawer for this
          // run, exclude the transit-supply SHAPES from the merged layer (Fixed
          // Routes supersedes OSM transit) — feature-level suppression now that
          // every set shares one layer.
          const shapes = suppressTransit
            ? layer.shapes.filter((shape) => !('description' in shape && shape.description === TRANSIT_SUPPLY_ROLE))
            : layer.shapes;
          const withShapes = shapes === layer.shapes ? layer : { ...layer, shapes };
          return prevHiddenById.has(layer.layerId)
            ? { ...withShapes, hidden: prevHiddenById.get(layer.layerId) as boolean }
            : withShapes;
        });
        return [...nonSpatial, ...spatialLayers];
      });
    }, [spatialAnalysis.run, gtfsLoadedRunId]);

    // Focus/zoom the map to the shapes of a generated sub-layer WITHIN the merged
    // spatial-analysis layer. The drawer calls this with the sub-layer's spatial
    // id ("<runId>:<slug>"); each merged shape's id is prefixed with its source
    // sub-layer id ("<runId>:<slug>:f<index>"), so we isolate the sub-layer by id
    // prefix (robust even where the slug differs from the role, e.g. anchors vs
    // amenity_anchor), falling back to the whole merged layer's bounds.
    const handleFocusSpatialLayer = useCallback((spatialLayerId: string) => {
      const map = mapRef.current;
      const run = spatialAnalysis.run;
      if (!map || !run) {
        return;
      }
      const mergedId = mergedSpatialLayerId(run.id);
      const merged = global.mapLayers.find((layer) => layer.layerId === mergedId);
      if (!merged) {
        return;
      }
      const subLayerShapes = merged.shapes.filter((shape) => shape.id.startsWith(`${spatialLayerId}:`));
      const targetShapes = subLayerShapes.length > 0 ? subLayerShapes : merged.shapes;

      const coords: [number, number][] = [];
      targetShapes.forEach((shape) => {
        if (shape.type === 'point') {
          coords.push(shape.coordinates);
        } else if (shape.type === 'line') {
          coords.push(...shape.coordinates);
        } else if (shape.type === 'polygon') {
          shape.coordinates.forEach((ring) => coords.push(...ring));
        }
      });
      if (coords.length > 0) {
        map.fitBounds(L.latLngBounds(coords.map(([lat, lng]) => [lat, lng])).pad(0.2));
      }
    }, [spatialAnalysis.run, global.mapLayers]);

    // Import the clipped GTFS feed into the Fixed Routes drawer (reusing the
    // GTFS->FixedRoutes converter) so real routes + stops + timetables render via
    // the fixed-routes method. Fire as soon as the feed is AVAILABLE — the backend
    // stores the clipped feed and flips `gtfsFeedAvailable` true during the
    // transit step, well before the run reaches `complete` (the LLM report draft
    // alone can add many seconds). We still fall back to `complete` for runs where
    // the flag was never set true. Importing early overlaps the remaining steps so
    // routes appear on the map sooner.
    //
    // Cancellation is keyed to run IDENTITY (latestRunIdRef), NOT a per-effect
    // boolean: this effect re-runs on every poll while the run is still active, so
    // a closure `cancelled` flag would abort the in-flight import on the very next
    // poll. Only a genuinely different/cleared run aborts an in-flight import.
    useEffect(() => {
      const run = spatialAnalysis.run;
      if (!run || run.status === 'cancelled' || run.status === 'failed') {
        return;
      }
      const feedReady = run.metrics.transit?.gtfsFeedAvailable === true;
      if (!feedReady && run.status !== 'complete') {
        return;
      }
      if (gtfsLoadedRunRef.current === run.id) {
        return;
      }
      const importRunId = run.id;
      gtfsLoadedRunRef.current = importRunId;
      const supersededByOtherRun = () => latestRunIdRef.current !== importRunId;
      setGtfsImporting(true);
      // Progress tracking: each phase is timed and logged to the console under the
      // `[SpatialAnalysis]` tag so the GTFS route-import pipeline (fetch -> convert
      // -> render) is observable. Watch the browser console while a run finishes.
      console.info(`[SpatialAnalysis] run ${importRunId}: starting GTFS route import…`);
      (async () => {
        try {
          // The feed is stored before `gtfsFeedAvailable` flips true, but allow a
          // few retries to cover any polling race before the feed is served.
          for (let attempt = 0; attempt < 3 && !supersededByOtherRun(); attempt++) {
            try {
              const fetchStart = performance.now();
              const feed = await getSpatialAnalysisGtfsFeed(importRunId);
              const fetchMs = Math.round(performance.now() - fetchStart);
              if (supersededByOtherRun()) {
                return;
              }
              if (!feed) {
                console.info(`[SpatialAnalysis] run ${importRunId}: no GTFS feed served yet (attempt ${attempt + 1}/3, fetch ${fetchMs}ms) — map shows OSM transit layer`);
                await new Promise((r) => setTimeout(r, 1500));
                continue;
              }
              const feedData = feed as unknown as GTFSFeedData;
              const convertStart = performance.now();
              const result = convertGTFSFeedToFixedRoutes(feedData, { preferredService: 'weekday' });
              const convertMs = Math.round(performance.now() - convertStart);
              console.info(
                `[SpatialAnalysis] run ${importRunId}: GTFS import — fetch ${fetchMs}ms, convert ${convertMs}ms ` +
                  `| feed ${feedData.routes?.length ?? 0} routes / ${feedData.stops?.length ?? 0} stops / ` +
                  `${feedData.trips?.length ?? 0} trips / ${feedData.shapes?.length ?? 0} shape pts ` +
                  `-> drew ${result.routes.length} routes, ${result.stops.length} stops ` +
                  `(skipped ${result.stats.skippedRoutes}, service ${result.serviceIdUsed})`,
              );
              if (result.routes.length > 0) {
                const renderStart = performance.now();
                bulkAddStopsRoutes(result.stops, result.routes, 'merge');
                setGtfsLoadedRunId(importRunId);
                console.info(
                  `[SpatialAnalysis] run ${importRunId}: handed ${result.routes.length} routes to the map ` +
                    `(${Math.round(performance.now() - renderStart)}ms) — lines render on the next paint`,
                );
              } else {
                console.warn(`[SpatialAnalysis] run ${importRunId}: converter produced 0 routes from the feed — Fixed Routes drawer will be empty`, result.warnings);
              }
              return;
            } catch (err) {
              console.error(`[SpatialAnalysis] run ${importRunId}: GTFS import failed`, err);
              gtfsLoadedRunRef.current = null; // allow a manual re-trigger to retry
              return;
            }
          }
        } finally {
          // Clear the indicator unless a newer run has taken over (its own import
          // owns the flag and will clear it when done).
          if (!supersededByOtherRun()) {
            setGtfsImporting(false);
          }
        }
      })();
    }, [spatialAnalysis.run, bulkAddStopsRoutes]);

    // Helper to swap [lat, lon] to [lon, lat] for API call only
    const swapPolygonCoordinates = useCallback((polygon: Polygon) => {
      // Return a GeoJSON geometry (type "Polygon"), not the internal shape whose
      // type is "polygon" — the backend rejects anything but "Polygon"/"MultiPolygon".
      return {
        type: 'Polygon',
        coordinates: polygon.coordinates.map(ring =>
          ring.map(([lat, lon]) => [wrapLongitude(lon), lat])
        ),
      };
    }, []);

    const MAX_POINTS = 2000;
    const MIN_POINTS = 500;
    const DEFAULT_MAX_POPULATION_POLYGON_AREA_KM2 = 1000;
    const DEFAULT_MAX_POPULATION_POLYGON_VERTEX_COUNT = 5000;
    const readNumericEnv = (key: string): number | undefined => {
      const processValue =
        typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
      const parsed = Number(processValue);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
      return undefined;
    };
    const rawMaxPopulationPolygonAreaKm2 =
      readNumericEnv('REACT_APP_MAX_POPULATION_POLYGON_AREA_KM2') ??
      readNumericEnv('VITE_MAX_POPULATION_POLYGON_AREA_KM2');
    const rawMaxPopulationPolygonVertexCount =
      readNumericEnv('REACT_APP_MAX_POPULATION_POLYGON_VERTEX_COUNT') ??
      readNumericEnv('VITE_MAX_POPULATION_POLYGON_VERTEX_COUNT');
    const maxPopulationPolygonAreaKm2: number =
      rawMaxPopulationPolygonAreaKm2 ?? DEFAULT_MAX_POPULATION_POLYGON_AREA_KM2;
    const maxPopulationPolygonVertexCount: number =
      rawMaxPopulationPolygonVertexCount ?? DEFAULT_MAX_POPULATION_POLYGON_VERTEX_COUNT;

    interface PopulationGridResponse {
      total_population: number;
      points?: unknown[];
      capped?: boolean;
      downsampled?: boolean;
    }

    const getPopulationPolygonLimitError = useCallback(
      (polygon: Polygon): string | null => {
        const outerRing = polygon.coordinates?.[0] ?? [];
        const vertexCount = outerRing.length;

        if (vertexCount > maxPopulationPolygonVertexCount) {
          return t('mapShell.populationErrors.tooDetailed', {
            vertexCount: vertexCount.toLocaleString(),
            maxVertexCount: maxPopulationPolygonVertexCount.toLocaleString(),
          });
        }

        if (vertexCount < 3) {
          return t('mapShell.populationErrors.polygonInvalid');
        }

        const lngLatRing = outerRing
          .filter(
            (point): point is [number, number] =>
              Array.isArray(point) &&
              point.length === 2 &&
              Number.isFinite(point[0]) &&
              Number.isFinite(point[1])
          )
          .map(([lat, lng]) => [lng, lat] as [number, number]);
        if (lngLatRing.length < 3) {
          return t('mapShell.populationErrors.polygonInvalid');
        }
        const firstPoint = lngLatRing[0];
        const lastPoint = lngLatRing[lngLatRing.length - 1];
        if (!firstPoint || !lastPoint) {
          return t('mapShell.populationErrors.polygonInvalid');
        }
        const isClosed = firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1];
        const closedRing = isClosed ? lngLatRing : [...lngLatRing, firstPoint];

        try {
          const areaKm2 = turf.area(turf.polygon([closedRing])) / 1_000_000;
          if (areaKm2 > maxPopulationPolygonAreaKm2) {
            return t('mapShell.populationErrors.tooLarge', {
              areaKm2: areaKm2.toFixed(1),
              maxAreaKm2: maxPopulationPolygonAreaKm2.toLocaleString(),
            });
          }
        } catch (error) {
          // Don't block population analysis if client-side area estimation fails on complex geometry.
          console.warn('Population polygon area validation skipped due geometry parsing issue:', error);
        }

        return null;
      },
      [maxPopulationPolygonAreaKm2, maxPopulationPolygonVertexCount, t]
    );

    const getPopulationPolygonLimitErrorById = useCallback(
      (polygonId: string): string | null => {
        const polygon = allPolygons.find((shape) => shape.id === polygonId);
        if (!polygon) {
          return t('mapShell.populationErrors.selectValidPolygon');
        }
        return getPopulationPolygonLimitError(polygon);
      },
      [allPolygons, getPopulationPolygonLimitError, t]
    );

    const fetchPopulationGridData = useCallback(
      async (
        polygon: Polygon,
        options?: {
          maxPoints?: number;
          setWarning?: (warning: string | null) => void;
        }
      ): Promise<{ totalPopulation: number; downsampled: boolean }> => {
        const polygonLimitError = getPopulationPolygonLimitError(polygon);
        if (polygonLimitError) {
          throw new Error(polygonLimitError);
        }

        const maxPoints = options?.maxPoints ?? MAX_POINTS;
        const swappedPolygon = swapPolygonCoordinates(polygon);
        const popResponse = await atlasFetch(`${getAtlasApiBaseUrl()}/api/population-grid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            polygon: swappedPolygon,
            year: 2020,
            demographic: 'UNadj',
            max_points: maxPoints,
            debug: true
          })
        });

        if (!popResponse.ok) {
          throw new Error(
            await getApiErrorDetail(popResponse, t('mapShell.populationErrors.apiError'))
          );
        }

        const data = (await popResponse.json()) as PopulationGridResponse;
        const pointCount = Array.isArray(data.points) ? data.points.length : 0;
        const isDownsampled = Boolean(data.capped || data.downsampled || pointCount >= MAX_POINTS);

        if (isDownsampled) {
          options?.setWarning?.(
            t('mapShell.populationErrors.downsampledLarge')
          );
        }

        if (pointCount >= MAX_POINTS && maxPoints > MIN_POINTS) {
          options?.setWarning?.(t('mapShell.populationErrors.downsampledVeryLarge'));
          return fetchPopulationGridData(polygon, {
            ...options,
            maxPoints: Math.floor(maxPoints / 2),
          });
        }

        return {
          totalPopulation: Math.round(Number(data.total_population || 0)),
          downsampled: isDownsampled,
        };
      },
      [getPopulationPolygonLimitError, swapPolygonCoordinates, t]
    );

    // Accepts a polygon object or a polygon ID
    const handleGetPopulationData = async (
      polygonOrId: Polygon | string,
      maxPoints: number = MAX_POINTS
    ): Promise<{ totalPopulation: number; coveredPopulation: number; uncoveredPopulation: number } | null> => {
      setPopulationLoading(true);
      setPopulationError(null);
      setPopulationTotal(null);
      setPopulationWarning(null);
      try {
        let polygon: Polygon | undefined;
        if (typeof polygonOrId === 'string') {
          polygon = allPolygons.find(
            p => p.id === polygonOrId && p.type === 'polygon'
          ) as Polygon | undefined;
        } else {
          polygon = polygonOrId;
        }
        if (!polygon || !global) return null;
        const applyPopulationFieldsToPolygon = (polygonId: string, updates: Partial<Polygon>) => {
          global.setLayers((prevLayers) =>
            prevLayers.map((layer) => {
              const hasTargetPolygon = layer.shapes.some(
                (shape) => shape.type === 'polygon' && shape.id === polygonId
              );
              if (!hasTargetPolygon) {
                return layer;
              }
              return {
                ...layer,
                shapes: layer.shapes.map((shape) => {
                  if (shape.type !== 'polygon' || shape.id !== polygonId) {
                    return shape;
                  }
                  return {
                    ...shape,
                    ...updates,
                  };
                }),
              };
            })
          );
        };

        // 1) Load total population first and update UI immediately.
        const popResponse = await fetchPopulationGridData(polygon, {
          maxPoints,
          setWarning: setPopulationWarning,
        });
        const totalPop = popResponse.totalPopulation;
        setPopulationTotal(totalPop);
        setCoveredPopulation(null);
        setUncoveredPopulation(null);
        setPopulationModalOpen(true);
        setBatchResults(null);
        applyPopulationFieldsToPolygon(polygon.id, {
          totalPopulation: totalPop,
          coveredPopulation: undefined,
          desertPopulation: undefined,
          percentCovered: undefined,
          percentDesert: undefined,
        });

        // 2) Run transit analysis in background. If it fails, keep total population data.
        setTransitError(null);
        void handleTransitAccessibilityAnalysis({
          polygon,
          global,
          setTransitLoading,
          setTransitError,
        })
          .then((transitResult) => {
            const coveredPop = Math.round(transitResult.coveredPopulation);
            const uncoveredPop = Math.round(transitResult.uncoveredPopulation);
            setCoveredPopulation(coveredPop);
            setUncoveredPopulation(uncoveredPop);
            const percentCovered = totalPop > 0 ? +(100 * coveredPop / totalPop).toFixed(1) : 0;
            const percentDesert = totalPop > 0 ? +(100 * uncoveredPop / totalPop).toFixed(1) : 0;
            applyPopulationFieldsToPolygon(polygon!.id, {
              totalPopulation: totalPop,
              coveredPopulation: coveredPop,
              desertPopulation: uncoveredPop,
              percentCovered,
              percentDesert,
            });
          })
          .catch((transitErr) => {
            console.warn('Transit coverage analysis failed (population total still loaded):', transitErr);
          });

        const populationResult = {
          totalPopulation: totalPop,
          coveredPopulation: 0,
          uncoveredPopulation: 0,
        };
        return populationResult;
      } catch (err: any) {
        setPopulationError(err.message || t('mapShell.populationErrors.fetchFailed'));
        setPopulationModalOpen(true);
        return null;
      } finally {
        setPopulationLoading(false);
      }
    };

    const [recentRideLayerId, setRecentRideLayerId] = useState<string | undefined>(undefined);
    const [recentFleetId, setRecentFleetId] = useState<string | undefined>(undefined);

    useEffect(() => {
      const defaults = simulationResultsCtx?.runSimulationDefaults;
      if (!defaults) return;
      if (defaults.rideLayerId) setRecentRideLayerId(defaults.rideLayerId);
      if (defaults.fleetId) setRecentFleetId(defaults.fleetId);
    }, [simulationResultsCtx?.runSimulationDefaults]);
    const [wizardServiceAreaPolygonId, setWizardServiceAreaPolygonId] = useState<string | null>(null);
    const ONBOARDING_WIZARD_LOCALSTORAGE_KEY = 'atlas_onboardingWizard_seen_v1';
    const userId = context.userData?.userDetails?.userId || '';
    const isSignedInUser = Boolean(userId);

    const polygonCount = useMemo(
      () =>
        global.mapLayers.reduce((count, layer) => {
          const visiblePolygonCount = layer.shapes.filter((shape) => shape.type === 'polygon').length;
          return count + visiblePolygonCount;
        }, 0),
      [global.mapLayers]
    );

    const pointCount = useMemo(
      () =>
        global.mapLayers.reduce((count, layer) => {
          const layerPoints = layer.shapes.filter((shape) => shape.type === 'point').length;
          return count + layerPoints;
        }, 0),
      [global.mapLayers]
    );

    const getNextWizardHubName = useCallback(() => {
      const quickStartHubPoints = global.mapLayers
        .flatMap((layer) => layer.shapes)
        .filter((shape): shape is Point => shape.type === 'point');

      return getNextQuickStartHubName(quickStartHubPoints);
    }, [global.mapLayers]);

    const wizardHubOptions = useMemo(() => {
      return global.mapLayers.flatMap((layer) =>
        layer.shapes
          .filter((shape): shape is Point => shape.type === 'point' && shape.creationSource === 'quick-start-hub')
          .map((point) => ({ id: point.id, name: point.name || t('mapShell.wizard.hubFallback', { id: point.id.slice(0, 4) }) }))
      );
    }, [global.mapLayers, t]);

    const rememberWizardServiceAreaPolygonId = useCallback((polygonId: string | null) => {
      if (!polygonId) {
        return;
      }
      setWizardServiceAreaPolygonId((prev) => prev ?? polygonId);
    }, []);

    const getPreferredWizardServiceAreaSelection = useCallback((): { polygon: Polygon; label: string } | undefined => {
      const allPolygonsWithLayer = global.mapLayers.flatMap((layer) =>
        layer.shapes
          .filter((shape): shape is Polygon => shape.type === 'polygon')
          .map((shape) => ({ polygon: shape, layerName: layer.layerName }))
      );

      const getSelectionLabel = (polygon: Polygon, layerName?: string) => {
        if (polygon.name && polygon.name !== 'New Polygon') {
          return polygon.name;
        }
        return layerName || t('mapShell.wizard.serviceAreaFallback');
      };

      if (wizardServiceAreaPolygonId) {
        const explicit = allPolygonsWithLayer.find(({ polygon }) => polygon.id === wizardServiceAreaPolygonId);
        if (explicit) {
          return {
            polygon: explicit.polygon,
            label: getSelectionLabel(explicit.polygon, explicit.layerName),
          };
        }
      }

      const derivedLayerNames = new Set(['Transit Coverage (400m)', 'Uncovered Area (Transit Desert)']);
      const nonDerived = [...allPolygonsWithLayer]
        .reverse()
        .find(({ layerName }) => !derivedLayerNames.has(layerName));
      if (nonDerived) {
        return {
          polygon: nonDerived.polygon,
          label: getSelectionLabel(nonDerived.polygon, nonDerived.layerName),
        };
      }

      const fallback = [...allPolygons]
        .reverse()
        .find((shape): shape is Polygon & { _layerName: string } => shape.type === 'polygon');
      if (!fallback) {
        return undefined;
      }
      return {
        polygon: fallback,
        label: getSelectionLabel(fallback, fallback._layerName),
      };
    }, [allPolygons, global.mapLayers, wizardServiceAreaPolygonId, t]);

    const preferredWizardServiceArea = useMemo(
      () => getPreferredWizardServiceAreaSelection(),
      [getPreferredWizardServiceAreaSelection]
    );

    const hasWizardServiceAreaPolygon = useMemo(
      () => Boolean(preferredWizardServiceArea),
      [preferredWizardServiceArea]
    );

    useEffect(() => {
      if (!isSignedInUser) {
        return;
      }
      const hasSeenWizard = localStorage.getItem(ONBOARDING_WIZARD_LOCALSTORAGE_KEY) === 'true';
      if (!hasSeenWizard) {
        setWizardEntrypoint('auto_open');
        setIsOnboardingWizardOpen(true);
        localStorage.setItem(ONBOARDING_WIZARD_LOCALSTORAGE_KEY, 'true');
      }
    }, [isSignedInUser]);

    useEffect(() => {
      const handleOpenWizard = () => {
        setWizardEntrypoint('nav_quick_start');
        setIsOnboardingWizardOpen(true);
      };
      window.addEventListener('openOnboardingWizard', handleOpenWizard);
      return () => window.removeEventListener('openOnboardingWizard', handleOpenWizard);
    }, []);

    const handleWizardDrawPolygon = useCallback(() => {
      setIsOnboardingWizardOpen(true);
      setWizardMapInteraction('wizard-polygon');
      setModeSettings((prev) => ({
        ...prev,
        mode: 'Draw Polygon',
        drawingLinePoints: [],
      }));
      setIsFixedRouteCaptureMode(false);
      setIsStopPickMode(false);
    }, [
      modeSettings.drawingLinePoints.length,
      modeSettings.drawingPolygonPoints.length,
      modeSettings.mode,
      setModeSettings,
      setIsFixedRouteCaptureMode,
      setIsStopPickMode,
      setWizardMapInteraction,
    ]);

    const handleWizardDrawHub = useCallback(() => {
      setIsOnboardingWizardOpen(true);
      let canSwitchToHubMode = false;
      setModeSettings((prev) => {
        const shouldFinalizeWizardPolygon = prev.mode === 'Draw Polygon' && prev.drawingPolygonPoints.length >= 3;
        const hasPolygonReady = hasWizardServiceAreaPolygon || shouldFinalizeWizardPolygon;
        if (!hasPolygonReady) {
          return prev;
        }
        canSwitchToHubMode = true;

        if (shouldFinalizeWizardPolygon) {
          const createdPolygonId = addPolygon(prev.drawingPolygonPoints);
          if (createdPolygonId) {
            rememberWizardServiceAreaPolygonId(createdPolygonId);
          }
        }

        return {
          ...prev,
          mode: 'Add Points',
          editingPolygonId: null,
          editingVertexIndex: null,
          drawingPolygonPoints: [],
          drawingLinePoints: [],
        };
      });
      if (!canSwitchToHubMode) {
        return;
      }
      setWizardMapInteraction('wizard-hub');
      setIsFixedRouteCaptureMode(false);
      setIsStopPickMode(false);
    }, [addPolygon, hasWizardServiceAreaPolygon, setModeSettings]);

    const handleWizardDemandInsight = useCallback(async (): Promise<DemandInsightResult> => {
      if (!global) {
        throw new Error(t('mapShell.wizard.errorGlobalContextUnavailable'));
      }
      const latestPolygon = preferredWizardServiceArea?.polygon;

      if (!latestPolygon) {
        throw new Error(t('mapShell.wizard.errorDrawServiceAreaForHelp'));
      }

      const populationResult = await fetchPopulationGridData(latestPolygon);
      const refreshedPolygon = global.mapLayers
        .flatMap((layer) => layer.shapes)
        .find((shape) => shape.type === 'polygon' && shape.id === latestPolygon.id) as Polygon | undefined;

      const totalPopulation = Math.max(
        0,
        Math.round(populationResult?.totalPopulation ?? refreshedPolygon?.totalPopulation ?? 0)
      );
      const coveredPopulation = totalPopulation;

      return {
        insight: {
          totalPopulation,
          coveredPopulation,
          transitPropensityPct: 25,
          estimatedAvgTripLengthKm: 4.8,
        },
        authoritativeRideLayerId: recentRideLayerId,
      };
    }, [fetchPopulationGridData, global, preferredWizardServiceArea, recentRideLayerId, t]);

    const handleWizardEditServiceAreaPolygon = useCallback(() => {
      const targetPolygon = preferredWizardServiceArea?.polygon;
      if (!targetPolygon) {
        throw new Error(t('mapShell.wizard.errorDrawServiceAreaForEdit'));
      }

      setWizardServiceAreaPolygonId(targetPolygon.id);
      global.setSelectedShapes([targetPolygon.id]);
      setModeSettings((prev) => ({
        ...prev,
        mode: 'Select',
        drawingPolygonPoints: [],
        drawingLinePoints: [],
        editingPolygonId: targetPolygon.id,
        editingVertexIndex: null,
      }));
      setIsFixedRouteCaptureMode(false);
      setIsStopPickMode(false);
    }, [global, preferredWizardServiceArea, setModeSettings, t]);

    const handleWizardGenerateTripsBackground = useCallback(async (payload: WizardGenerateTripsPayload): Promise<string> => {
      const latestPolygon = preferredWizardServiceArea?.polygon;
      if (!latestPolygon) {
        throw new Error(t('mapShell.wizard.errorNoPolygonForTrips'));
      }

      const allPoints = global.mapLayers.flatMap((layer) => layer.shapes).filter((shape): shape is Point => shape.type === 'point');
      const meetingPoints =
        payload.useMeetingPointsOnly && payload.meetingPointLayerId
          ? global.mapLayers
              .find((layer) => String(layer.layerId) === payload.meetingPointLayerId)
              ?.shapes.filter((shape): shape is Point => shape.type === 'point') ?? []
          : [];
      const selectedHubs = payload.hubWeights
        .map((hub: HubWeight) => {
          const point = allPoints.find((shape) => shape.id === hub.id);
          if (!point) return null;
          return {
            id: point.id,
            name: point.name || hub.name,
            coordinates: point.coordinates as [number, number],
            percentage: hub.percentage,
          };
        })
        .filter(
          (
            hub
          ): hub is {
            id: string;
            name: string;
            coordinates: [number, number];
            percentage: number;
          } => hub !== null
        );

      const modeByServiceMode: Record<WizardGenerateTripsPayload['serviceMode'], 'Point to Point' | 'Point to Hub' | 'Hub to Hub'> =
        {
          door_to_door: 'Point to Point',
          door_to_hub: 'Point to Hub',
          hub_to_hub: 'Hub to Hub',
        };

      const requestedRides = Math.max(1, Math.round(payload.numberOfRides));
      const defaultWindows =
        payload.servicePreset === 'school'
          ? [
              { startUtc: '07:00', endUtc: '09:00' },
              { startUtc: '14:00', endUtc: '16:00' },
            ]
          : [{ startUtc: '06:00', endUtc: '22:00' }];
      const sourceWindows = payload.operatingPeriodsUtc.length > 0 ? payload.operatingPeriodsUtc : defaultWindows;

      const toMinutes = (time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const buildWindows = (periods: Array<{ startUtc: string; endUtc: string }>) =>
        periods
        .map((period) => {
          const startUtc = period.startUtc;
          const endUtc = period.endUtc;
          const durationMinutes = toMinutes(endUtc) - toMinutes(startUtc);
          return { startUtc, endUtc, durationMinutes };
        })
        .filter((period) => period.durationMinutes > 0);

      let windows = buildWindows(sourceWindows);
      if (windows.length === 0) {
        windows = buildWindows(defaultWindows);
      }

      if (windows.length === 0) {
        throw new Error(t('mapShell.wizard.errorNoValidWindows'));
      }

      const totalDuration = windows.reduce((sum, window) => sum + window.durationMinutes, 0);
      const baseAllocations = windows.map((window, index) => {
        const exactShare = (requestedRides * window.durationMinutes) / totalDuration;
        const allocated = Math.floor(exactShare);
        return {
          index,
          rides: allocated,
          fractionalRemainder: exactShare - allocated,
        };
      });

      let ridesRemaining = requestedRides - baseAllocations.reduce((sum, allocation) => sum + allocation.rides, 0);
      const remainderOrder = [...baseAllocations].sort((a, b) => b.fractionalRemainder - a.fractionalRemainder);
      let remainderCursor = 0;
      while (ridesRemaining > 0 && remainderOrder.length > 0) {
        const target = remainderOrder[remainderCursor % remainderOrder.length];
        baseAllocations[target.index].rides += 1;
        ridesRemaining -= 1;
        remainderCursor += 1;
      }

      const generatedBatches = baseAllocations
        .filter((allocation) => allocation.rides > 0)
        .map((allocation) =>
          generateTripsFromAgentAnswers(
            {
              numberOfRides: allocation.rides,
              startDate: `${DEFAULT_SIMULATION_DATE} ${windows[allocation.index].startUtc}:00`,
              endDate: `${DEFAULT_SIMULATION_DATE} ${windows[allocation.index].endUtc}:00`,
              mode: modeByServiceMode[payload.serviceMode],
              geofencePolygon: latestPolygon,
              hubs: selectedHubs,
              passengerTypes: [
                {
                  passenger_type: 'standard',
                  processing_time: 30,
                  probability: payload.passengerDistribution.standardPct / 100,
                },
                {
                  passenger_type: 'wheelchair',
                  processing_time: 90,
                  probability: payload.passengerDistribution.wheelchairPct / 100,
                },
              ],
              useMeetingPointsOnly: payload.useMeetingPointsOnly,
              meetingPoints,
            },
            global.mapLayers
          )
        );

      if (generatedBatches.length === 0) {
        throw new Error(t('mapShell.wizard.errorTripGenerationNoRides'));
      }

      const batchedShapes = generatedBatches.flatMap((batch) => batch.layer.shapes);
      const batchedTripLineCount = batchedShapes.filter((shape) => isCanonicalTripLineShape(shape)).length;
      if (batchedTripLineCount === 0) {
        throw new Error(t('mapShell.wizard.errorTripGenerationNoLines'));
      }

      // Ensure wizard-generated trip lines always include origin/destination point links
      // so TripLayerRenderer can apply the same render cap used in other trip flows.
      const syntheticTripPoints: Point[] = [];
      const normalizedMergedShapes = batchedShapes.map((shape, index) => {
        if (shape.type !== 'line') {
          return shape;
        }

        const line = shape as Line & Partial<{ originPointId: string; destPointId: string }>;
        if (line.originPointId && line.destPointId) {
          return line;
        }

        const startCoord = line.coordinates?.[0];
        const endCoord = line.coordinates?.[line.coordinates.length - 1];
        const hasValidEndpoints =
          Array.isArray(startCoord) &&
          Array.isArray(endCoord) &&
          startCoord.length >= 2 &&
          endCoord.length >= 2 &&
          Number.isFinite(startCoord[0]) &&
          Number.isFinite(startCoord[1]) &&
          Number.isFinite(endCoord[0]) &&
          Number.isFinite(endCoord[1]);

        if (!hasValidEndpoints) {
          return line;
        }

        const originPointId = `wizard-trip-origin-${uuid4()}`;
        const destPointId = `wizard-trip-dest-${uuid4()}`;
        syntheticTripPoints.push(
          {
            id: originPointId,
            layerId: -1,
            type: 'point',
            name: t('mapShell.wizard.tripOrigin', { id: originPointId.slice(0, 6) }),
            description: t('mapShell.wizard.tripOriginDescription'),
            colour: '#FF0000',
            icon: 'dot',
            size: 8,
            isLocked: true,
            coordinates: [startCoord[0], startCoord[1]],
            hidden: true,
          },
          {
            id: destPointId,
            layerId: -1,
            type: 'point',
            name: t('mapShell.wizard.tripDestination', { id: destPointId.slice(0, 6) }),
            description: t('mapShell.wizard.tripDestinationDescription'),
            colour: '#00FF00',
            icon: 'dot',
            size: 8,
            isLocked: true,
            coordinates: [endCoord[0], endCoord[1]],
            hidden: true,
          }
        );

        return {
          ...line,
          originPointId,
          destPointId,
          ...lineAddressPlaceholders(line as unknown as Record<string, unknown>, index + 1),
        };
      });

      const normalizedShapesWithFallbackPoints = [...normalizedMergedShapes, ...syntheticTripPoints];
      const tripLinesWithPointLinks = normalizedShapesWithFallbackPoints.filter(
        (shape): shape is Line & Partial<{ originPointId: string; destPointId: string }> =>
          shape.type === 'line' && isCanonicalTripLineShape(shape)
      );
      const tripPointIds = new Set<string>();
      tripLinesWithPointLinks.forEach((line) => {
        if (line.originPointId) tripPointIds.add(line.originPointId);
        if (line.destPointId) tripPointIds.add(line.destPointId);
      });

      // Keep wizard trip-point rendering consistent with Generate Trips UX:
      // show the same preview subset as capped trip lines, keep full data in-state.
      const visibleTripPointIds = new Set<string>();
      tripLinesWithPointLinks.slice(0, TRIP_RENDER_CAP).forEach((line) => {
        if (line.originPointId) visibleTripPointIds.add(line.originPointId);
        if (line.destPointId) visibleTripPointIds.add(line.destPointId);
      });
      const dimmedPolygonOpacity = '0.22';
      let resolvedLayerId = 0;
      global.setLayers((prev) => {
        const { nextLayerId, reusableLayer } = resolveWizardTripLayerTarget(prev, recentRideLayerId);
        resolvedLayerId = nextLayerId;

        const normalizedShapes = normalizedMergedShapes.map((shape) => ({
          ...shape,
          layerId: nextLayerId,
        }));
        const syntheticPoints = syntheticTripPoints.map((point) => ({
          ...point,
          layerId: nextLayerId,
        }));
        const previewShapesForLayer = normalizedShapes
          .concat(syntheticPoints)
          .map((shape) => {
            if (shape.type !== 'point') {
              return shape;
            }
            if (!tripPointIds.has(shape.id)) {
              return shape;
            }
            return {
              ...shape,
              hidden: !visibleTripPointIds.has(shape.id),
            };
          });

        const mergedLayer: Layer = {
          layerId: nextLayerId,
          layerName: reusableLayer?.layerName || t('mapShell.wizard.generatedTripsLayer', { n: nextLayerId + 1 }),
          hidden: false,
          renderAll: false,
          shapes: previewShapesForLayer,
        };

        const dimmed = prev.map((layer) => ({
          ...layer,
          shapes: layer.shapes.map((shape) => {
            if (shape.type !== 'polygon' || shape.id !== latestPolygon.id) {
              return shape;
            }
            return {
              ...shape,
              opacity: dimmedPolygonOpacity,
            };
          }),
        }));

        if (reusableLayer) {
          return dimmed.map((layer) => (layer.layerId === reusableLayer.layerId ? mergedLayer : layer));
        }

        return [...dimmed, mergedLayer];
      });
      global.setActiveLayerId(resolvedLayerId);
      setRecentRideLayerId(String(resolvedLayerId));
      setFitBoundsScope('lastLayer');
      setShouldFitBounds(true);
      return String(resolvedLayerId);
    }, [global, preferredWizardServiceArea, recentRideLayerId, setFitBoundsScope, setShouldFitBounds, t]);

    const getOrCreateWizardTripCentroidPoint = useCallback((rideLayer: Layer) => {
      const tripLines = getCanonicalTripLines(rideLayer);
      const endpoints: [number, number][] = [];
      tripLines.forEach((line) => {
        if (!line.coordinates || line.coordinates.length < 2) return;
        endpoints.push(line.coordinates[0], line.coordinates[line.coordinates.length - 1]);
      });

      if (endpoints.length === 0) {
        throw new Error(t('mapShell.wizard.errorTripLayerNoCoordinates'));
      }

      const centroid: [number, number] = [
        endpoints.reduce((sum, coord) => sum + coord[0], 0) / endpoints.length,
        endpoints.reduce((sum, coord) => sum + coord[1], 0) / endpoints.length,
      ];

      const allPoints = global.mapLayers.flatMap((layer) => layer.shapes).filter((shape): shape is Point => shape.type === 'point');
      const tolerance = 0.0001;
      const existing = allPoints.find((point) => {
        const distance = Math.sqrt(
          Math.pow(point.coordinates[0] - centroid[0], 2) + Math.pow(point.coordinates[1] - centroid[1], 2)
        );
        return distance < tolerance;
      });
      if (existing) {
        return {
          pointId: existing.id,
          coordinates: { lat: centroid[0], lng: centroid[1] },
        };
      }

      const centroidPoint: Point = {
        id: `wizard-trip-centroid-${Date.now()}`,
        type: 'point',
        layerId: rideLayer.layerId,
        name: t('mapShell.wizard.tripLayerCentroid'),
        description: t('mapShell.wizard.tripLayerCentroidDescription'),
        colour: '#FF9800',
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: centroid,
        hidden: false,
      };

      global.setLayers((prev) =>
        prev.map((layer) =>
          layer.layerId === rideLayer.layerId ? { ...layer, shapes: [...layer.shapes, centroidPoint] } : layer
        )
      );

      return {
        pointId: centroidPoint.id,
        coordinates: { lat: centroid[0], lng: centroid[1] },
      };
    }, [global.mapLayers, global.setLayers, t]);

    const isWizardRideLayerIdUsable = useCallback((rideLayerId: string, requireTimeData: boolean = true) => {
      if (!rideLayerId) return false;
      const { layer } = selectWizardTripLayer(global.mapLayers, {
        authoritativeRideLayerId: rideLayerId,
        requireTimeData,
      });
      return Boolean(layer && String(layer.layerId) === rideLayerId);
    }, [global.mapLayers]);

    const upsertWizardFleet = useCallback((incomingFleet: Fleet): string => {
      let resolvedFleetId = incomingFleet.id;
      setFleets((prev) => {
        const result = upsertWizardFleetList(prev, incomingFleet);
        resolvedFleetId = result.resolvedFleetId;
        return result.fleets;
      });
      return resolvedFleetId;
    }, [setFleets]);

    const handleWizardCreateManualFleet = useCallback(async (payload: WizardFleetConfigPayload): Promise<string> => {
      const vehicleCount = Math.max(1, Math.round(payload.vehicleCount));
      const standardSeats = Math.max(0, Math.round(payload.capacityStandard));
      const wheelchairSeats = Math.max(0, Math.round(payload.capacityWheelchair));

      if (standardSeats + wheelchairSeats <= 0) {
        throw new Error(t('mapShell.wizard.errorVehicleCapacityZero'));
      }

      const { layer: selectedRideLayer } = selectWizardTripLayer(global.mapLayers, {
        recentRideLayerId,
        requireTimeData: false,
      });
      if (!selectedRideLayer) {
        throw new Error(t('mapShell.wizard.errorNoTripLayerForFleet'));
      }

      const centroidPoint = getOrCreateWizardTripCentroidPoint(selectedRideLayer);
      const operatingPeriod = {
        start: '06:00',
        end: '22:00',
        breaks: [],
      };
      const shiftTemplates = (payload.shifts || []).map((shift) => ({
        vehicleName: shift.vehicleName,
        operatingPeriods: (shift.operatingPeriods || [])
          .filter((period) => parseTime(period.end) > parseTime(period.start))
          .map((period) => ({
            start: period.start,
            end: period.end,
            breaks: (period.breaks || []).map((breakPeriod) => ({
              start: breakPeriod.start,
              end: breakPeriod.end,
              location: breakPeriod.location || t('mapShell.wizard.breakLocationOnRoute'),
            })),
          })),
      }));
      const hasShiftTemplates = shiftTemplates.length > 0;
      const effectiveVehicleCount = hasShiftTemplates ? shiftTemplates.length : vehicleCount;

      const vehicles: Vehicle[] = Array.from({ length: effectiveVehicleCount }, (_, index) => ({
        id: `wizard-manual-vehicle-${Date.now()}-${index + 1}`,
        name: shiftTemplates[index]?.vehicleName || t('mapShell.wizard.vehicleName', { n: index + 1 }),
        standardSeats,
        wheelchairSeats,
        startLocation: centroidPoint.pointId,
        endLocation: centroidPoint.pointId,
        startLocationCoordinates: centroidPoint.coordinates,
        endLocationCoordinates: centroidPoint.coordinates,
        operatingHours: shiftTemplates[index]?.operatingPeriods.length
          ? shiftTemplates[index].operatingPeriods
          : [operatingPeriod],
      }));

      const newFleet: Fleet = {
        id: `fleet-${Date.now()}`,
        name: t('mapShell.wizard.fleetName', { time: new Date().toLocaleTimeString() }),
        vehicles,
      };
      const resolvedFleetId = upsertWizardFleet(newFleet);
      setRecentFleetId(resolvedFleetId);
      return resolvedFleetId;
    }, [getOrCreateWizardTripCentroidPoint, global.mapLayers, recentRideLayerId, upsertWizardFleet, t]);

    const handleWizardGenerateFleetFromDemand = useCallback(
      async (payload: {
        authoritativeRideLayerId?: string | null;
        servicePreset?: ServicePreset | null;
        capacityStandard: number;
        capacityWheelchair: number;
      }): Promise<WizardFleetHelpResult> => {
        const selection = selectWizardTripLayer(global.mapLayers, {
          authoritativeRideLayerId: payload.authoritativeRideLayerId ?? null,
          recentRideLayerId: recentRideLayerId ?? null,
          requireTimeData: true,
        });
        const selectedRideLayer = selection.layer;

        if (!selectedRideLayer) {
          if (selection.failureReason === 'no_valid_time_data') {
            throw new Error(t('mapShell.wizard.errorTripLayerNoTimeData'));
          }
          throw new Error(t('mapShell.wizard.errorNoTripLayerForDemand'));
        }

        const centroidPoint = getOrCreateWizardTripCentroidPoint(selectedRideLayer);
        const generated = generateFleetFromTripLayerAnalysis({
          layer: selectedRideLayer,
          centroidPoint,
          capacityStandard: payload.capacityStandard,
          capacityWheelchair: payload.capacityWheelchair,
          servicePreset: payload.servicePreset,
          selectedLayerId: String(selectedRideLayer.layerId),
        });
        const vehicles = generated.vehicles;
        const shifts = generated.shifts;
        const summary = generated.summary;

        const fleetName = t('mapShell.wizard.autoFleetName', { time: new Date().toLocaleTimeString() });
        const newFleet: Fleet = {
          id: `fleet-${Date.now()}`,
          name: fleetName,
          vehicles,
        };
        const resolvedFleetId = upsertWizardFleet(newFleet);
        setRecentFleetId(resolvedFleetId);

        return {
          fleetId: resolvedFleetId,
          fleetName,
          shifts: shifts.map((shift) => ({
            vehicleId: shift.vehicleId,
            vehicleName: shift.vehicleName,
            operatingPeriods: shift.operatingPeriods,
          })),
          summary: {
            ...summary,
          },
          serviceStartTime: generated.config.serviceStartTime,
          serviceEndTime: generated.config.serviceEndTime,
          peakPeriods: generated.peakPeriods,
        };
      },
      [getOrCreateWizardTripCentroidPoint, global.mapLayers, recentRideLayerId, setRecentFleetId, upsertWizardFleet, t]
    );

    const handleWizardRunSimulation = useCallback(async (payload: WizardRunPayload) => {
      openRightDrawer('simulationResults');
      setSimulationError(null);
      setStatus('running');
      try {
        const { layer: selectedRideLayer } = selectWizardTripLayer(global.mapLayers, {
          authoritativeRideLayerId: payload.authoritativeRideLayerId ?? null,
          recentRideLayerId: recentRideLayerId ?? null,
          requireTimeData: false,
        });
        if (!selectedRideLayer) {
          throw new Error(t('mapShell.wizard.errorNoRideLayerForSim'));
        }

        const rideLines = getCanonicalTripLines(selectedRideLayer);
        if (rideLines.length === 0) {
          throw new Error(t('mapShell.wizard.errorRideLayerNoTrips'));
        }

        const selectedFleet = ((payload.authoritativeFleetId ?? recentFleetId)
          ? fleets.find((fleet) => fleet.id === (payload.authoritativeFleetId ?? recentFleetId))
          : undefined) ?? fleets[fleets.length - 1];
        if (!selectedFleet) {
          throw new Error(t('mapShell.wizard.errorNoFleetForSim'));
        }

      const generatedRides = rideLines.map((line, index) => {
        const from = line.coordinates?.[0];
        const to = line.coordinates?.[line.coordinates.length - 1];
        if (!from || !to) {
          throw new Error(t('mapShell.wizard.errorTripLayerInvalidCoords'));
        }
        const standardCount = Number(line.standard_seat ?? line.numPassengers ?? 0);
        const wheelchairCount = Number(line.wheelchair ?? 0);
        const passengers = [
          ...(standardCount > 0 ? [{ number_of_passengers: standardCount, type: 'standard' as const }] : []),
          ...(wheelchairCount > 0 ? [{ number_of_passengers: wheelchairCount, type: 'wheelchair' as const }] : []),
        ];
        return {
          id: line.ride_id || line.id || `wizard-ride-${index + 1}`,
          from_latitude: from[0],
          from_longitude: from[1],
          to_latitude: to[0],
          to_longitude: to[1],
          request_type: (line.request_type || (line as Line & { requestType?: string }).requestType || 'departure_fixed'),
          request_time: String(
            (line as Line & { request_time?: string | number }).request_time ??
              line.time ??
              `${DEFAULT_SIMULATION_DATE}T08:00:00.000`
          ),
          passengers: passengers.length > 0 ? passengers : [{ number_of_passengers: 1, type: 'standard' as const }],
        };
      });
      const rides = mapGeneratedRidesToApi(generatedRides);

      const allPointShapes = global.mapLayers.flatMap((layer) => layer.shapes).filter((shape): shape is Point => shape.type === 'point');
      const fleetApiSettings = mapFleetToApiFleetSettings(selectedFleet, allPointShapes, rides[0]?.request_time);
      const vehicleCount = fleetApiSettings.vehicles.reduce(
        (sum: number, vehicle: any) => sum + Number(vehicle.number_of_vehicles || 0),
        0
      );

      const timeWindowSettings = buildTimeWindowSettings(
        Math.max(0.1, payload.feelSlope),
        Math.max(0, payload.feelInterceptMinutes) * 60,
        1200,
        15,
        [
          { seatType: 'standard', processingTimeSeconds: 30 },
          { seatType: 'wheelchair', processingTimeSeconds: 300 },
        ],
        payload.simulationName,
        Math.max(1, payload.maxTripTimeMinutes),
        ''
      );

      const simulationConfig = simulationService.createEnhancedSimulationConfig(
        payload.simulationName,
        rides,
        fleetApiSettings,
        {
          optimizationLevel: 'balanced',
          travelTimeMultiplier: 1.1,
          timeWindowSettings,
        }
      );

        setSimulationName(payload.simulationName);
        setNumberOfRides(rides.length);
        setNumberOfVehicles(vehicleCount);
        const results = await simulationService.runSimulation(
          simulationConfig,
          () => undefined,
          setStepStatuses
        );
        const runId = results.results_id || `wizard-run-${Date.now()}`;
        const successRatio = results.summary.total_rides > 0
          ? results.summary.assigned_rides / results.summary.total_rides
          : 0;

        const runSummary: SimulationRunSummary = {
          run_id: runId,
          results_id: results.results_id,
          scenario_name: payload.simulationName,
          run_time: new Date().toISOString(),
          success_ratio: successRatio,
          no_rides: results.summary.total_rides,
          no_vehicles: results.summary.total_vehicles ?? vehicleCount,
          allocated_vehicle_shifts: results.summary.total_vehicles ?? vehicleCount,
          ride_points: rides,
          metrics_fetched: false,
        };
        setSimulationHistory((prev) => [runSummary, ...prev.filter((run) => run.run_id !== runId)]);
        setSimulationId(runId);
        setStatus('finished');
      } catch (error) {
        const normalized = SimulationService.normalizeError(error);
        setSimulationError(normalized);
        setSimulationId('');
        setStatus('failed');
        throw normalized;
      }
    }, [
      recentRideLayerId,
      global.mapLayers,
      recentFleetId,
      fleets,
      setSimulationName,
      setNumberOfRides,
      setNumberOfVehicles,
      setSimulationError,
      setStatus,
      openRightDrawer,
      setSimulationHistory,
      setSimulationId,
      setStepStatuses,
      t,
    ]);

    const hasWheelchairTripsForRideLayer = useCallback(
      (rideLayerId: string) => {
        const rideLayer = global.mapLayers.find((layer) => String(layer.layerId) === String(rideLayerId));
        if (!rideLayer) {
          return false;
        }
        const tripLines = getCanonicalTripLines(rideLayer);
        return tripLines.some((line) => Number(line.wheelchair ?? 0) > 0);
      },
      [global.mapLayers]
    );

    // --- Auto-select new polygons and handle deletion of selected polygon ---
    const prevPolygonIds = React.useRef<string[]>([]);
    useEffect(() => {
      const currentIds = allPolygons.map(p => p.id);
      const prevIds = prevPolygonIds.current;

      // Only auto-select if nothing is currently selected
      if (
        (!selectedAnalysisPolygonId || !currentIds.includes(selectedAnalysisPolygonId)) &&
        currentIds.length > 0
      ) {
        // If a new polygon was added and nothing is selected, select the newest
        const newIds = currentIds.filter(id => !prevIds.includes(id));
        if (newIds.length > 0) {
          setSelectedAnalysisPolygonId(newIds[newIds.length - 1]);
        } else {
          // If no new polygon, but none is selected, select the first available
          setSelectedAnalysisPolygonId(currentIds[0]);
        }
      }

      prevPolygonIds.current = currentIds;
    }, [allPolygons.length, selectedAnalysisPolygonId]);

    return (
        <>
            <div 
                {...getRootProps()} 
                style={{ 
                    position: 'relative', 
                    height: `calc(100vh - ${MAP_UI_TOP_BAR_HEIGHT_PX}px)`,
                    width: '100%',
                    pointerEvents: 'auto'
                }}
                data-testid="map.shell.root"
                data-map-ready={mapReady ? 'true' : 'false'}
                data-drawer-state={activeRightDrawer ?? 'none'}
            >
                <input {...getInputProps()} />
                <MapSidebar
                  activeRightDrawer={activeRightDrawer}
                  onToggleRightDrawer={toggleRightDrawer}
                  onZoomIn={() => mapRef.current?.zoomIn()}
                  onZoomOut={() => mapRef.current?.zoomOut()}
                  wizardActive={isOnboardingWizardOpen}
                  gtfsImporting={gtfsImporting}
                />
                <FeatureGate featureName="population_analysis">
                  <div data-testid="map.drawer.population-analysis" data-open={isPopulationAnalysisDrawerOpen ? 'true' : 'false'}>
                  <PopulationAnalysisDrawer
                    open={isPopulationAnalysisDrawerOpen}
                    onClose={closeRightDrawer}
                    polygons={allPolygons}
                    selectedPolygonId={selectedAnalysisPolygonId}
                    onPolygonChange={setSelectedAnalysisPolygonId}
                    onValidatePopulationPolygon={getPopulationPolygonLimitErrorById}
                    transitError={transitError}
                    transitLoading={transitLoading}
                    onGetPopulationData={async (polygonId) => {
                      await handleGetPopulationData(polygonId);
                    }}
                    setRecentRideLayerId={setRecentRideLayerId}
                    setRecentFleetId={setRecentFleetId}
                  />
                  </div>
                </FeatureGate>
                <FeatureGate featureName="data_transfer_drawer">
                <FeatureGate featureName={ENVIRONMENT_TOKENS_FLAG}>
                  <div data-testid="map.drawer.data-transfer" data-open={isDataTransferDrawerOpen ? 'true' : 'false'}>
                  <DataTransferDrawer
                    open={isDataTransferDrawerOpen}
                    onClose={() => {
                      closeRightDrawer();
                      setDataTransferDrawerInitialTab(undefined);
                    }}
                    initialDataType={dataTransferDrawerInitialTab as any}
                  />
                  </div>
                </FeatureGate>
                </FeatureGate>
                <div data-testid="map.drawer.bug-report" data-open={isBugReportDrawerOpen ? 'true' : 'false'}>
                <BugReportDrawer
                  open={isBugReportDrawerOpen}
                  onClose={closeRightDrawer}
                  activeLayerId={activeLayerId}
                  mapZoom={mapSettings.mapCentre.zoom}
                />
                </div>
        <div data-testid="map.drawer.fleet-menu" data-open={isFleetMenuOpen ? 'true' : 'false'}>
        <FleetMenu
          open={isFleetMenuOpen}
          onClose={closeRightDrawer}
          fleets={fleets}
          setFleets={setFleets}
          editFleetTrigger={editFleetTrigger}
          rides={context?.global.mapLayers.flatMap(layer =>
            layer.shapes.filter(s => s.type === 'point').map(p => ({
              from_latitude: (p as any).latitude || 0,
              from_longitude: (p as any).longitude || 0,
              to_latitude: (p as any).latitude || 0,
              to_longitude: (p as any).longitude || 0,
              passengers: [{ number_of_passengers: 1, type: 'standard' }],
              request_time: getDefaultRequestTime()
            }))
          ) || []}
        />
        </div>
                <div data-testid="map.drawer.simulation-results" data-open={isSimulationResultsMenuOpen ? 'true' : 'false'}>
                <SimulationResultsMenu
                    open={isSimulationResultsMenuOpen}
                    onClose={closeRightDrawer}
                />
                <SpatialAnalysisDrawer
                    open={isSpatialAnalysisDrawerOpen}
                    onClose={closeRightDrawer}
                    polygons={allPolygons}
                    onRunSpatialAnalysis={handleRunSpatialAnalysis}
                    onFocusLayer={handleFocusSpatialLayer}
                    gtfsImporting={gtfsImporting}
                />
                <HostedNetworkPickerDrawer
                    open={networkPickerOpen}
                    onClose={() => setNetworkPickerOpen(false)}
                    selectedFeedId={networkFeedId}
                    onSelectFeed={selectNetworkFeed}
                    statusText={
                      networkStatus
                        ? networkStatus.truncated
                          ? `Showing ${networkStatus.shown} of ${networkStatus.total} routes — zoom in for fewer/sharper`
                          : `${networkStatus.shown} routes in view`
                        : null
                    }
                />
                <NetworkFeedDropHandler />
                </div>
                {activeRightDrawer !== null && activeRightDrawer !== 'fixedRoutes' && activeRightDrawer !== 'populationAnalysis' && activeRightDrawer !== 'spatialAnalysis' && (
                  <Box
                    onClick={closeRightDrawer}
                    aria-label={t('mapShell.closeActiveDrawer')}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2050,
                      backgroundColor: 'rgba(15, 23, 42, 0.12)',
                      backdropFilter: 'blur(1px)',
                      WebkitBackdropFilter: 'blur(1px)',
                      cursor: 'pointer',
                    }}
                  />
                )}
                <HighlightProvider>
                <div data-testid="map.leaflet.container" style={{ height: '100%', width: '100%' }}>
                <MapContainer
                    center={[mapSettings.mapCentre.latitude, mapSettings.mapCentre.longitude]}
                    zoom={mapSettings.mapCentre.zoom}
                    style={
                        {
                            height: '100%',
                            width: '100%',
                            cursor: mapCursor,
                            ['--map-ui-inset' as string]: `${MAP_UI_CORNER_INSET_PX}px`,
                        } as React.CSSProperties
                    }
                    ref={mapRef as any}
                    zoomControl={false}
                    className="atlas-main-map"
                    data-testid="map.leaflet.container"
                    whenReady={() => setMapReady(true)}
                >
                    <MapRecenter center={mapSettings.mapCentre} zoom={mapSettings.mapCentre.zoom} />
                    <MapViewportSync onViewportChange={mapSettings.setMapCentre} />
                    {children}
                    <MapEventsWrapper />
                    {googleBasemap.apiKey && (
                      <GoogleTileLayer
                        apiKey={googleBasemap.apiKey}
                        mapType={googleMapTypeForBasemap(mapSettings.basemap)}
                        extraAttribution="© OpenStreetMap contributors"
                        onUnavailable={handleGoogleBasemapUnavailable}
                      />
                    )}
                    {!googleBasemap.active && <TileLayer 
                        url={basemapOptions[mapSettings.basemap as keyof typeof basemapOptions]?.url?.() ?? basemapOptions.standard.url()}
                        attribution={basemapOptions[mapSettings.basemap as keyof typeof basemapOptions]?.attribution ?? basemapOptions.standard.attribution}
                        eventHandlers={{
                          tileerror: () => {
                            if (mapSettings.basemap !== 'standard') {
                              setBasemapLoadError(t('mapShell.basemapUnavailable', { name: mapSettings.basemap }));
                              mapSettings.setBasemap('standard');
                            }
                          }
                        }}
                    />}
                    <MapContextMenusManager
                      contextMenu={contextMenu}
                      setContextMenu={setContextMenu}
                      polygonContextMenu={polygonContextMenu}
                      setPolygonContextMenu={setPolygonContextMenu}
                      lineContextMenu={lineContextMenu}
                      setLineContextMenu={setLineContextMenu}
                      onEditPointStyle={(point) => {
                        setSelectedPoint(point);
                        setIsPointModalOpen(true);
                      }}
                      onEditPolygonStyle={(polygon) => {
                        setSelectedPolygon(polygon);
                        setIsPolygonModalOpen(true);
                      }}
                      onImportOSMLocations={(polygon) => {
                        if (!isFeatureRuntimeEnabled('import_osm_locations')) {
                          return;
                        }
                        setOsmLocationsPolygon(polygon);
                        setIsOsmLocationsModalOpen(true);
                      }}
                      onGenerateMeetingPoints={(polygon) => {
                        if (!isFeatureRuntimeEnabled('meeting_points_generator')) {
                          return;
                        }
                        setMeetingPointsPolygon(polygon);
                        setIsMeetingPointsModalOpen(true);
                      }}
                      onRunSpatialAnalysis={handleRunSpatialAnalysis}
                      onCreateSpatialAnalysisTicket={handleCreateSpatialAnalysisTicket}
                      isCreatingSpatialAnalysisTicket={isCreatingSpatialAnalysisTicket}
                      onEditLineStyle={(line) => {
                        setSelectedLine(line);
                        setIsLineModalOpen(true);
                      }}
                      onDeleteLine={(line) => {
                        const layer = global.mapLayers.find(l => l.layerId === activeLayerId);
                        if (layer) {
                          const updatedShapes = layer.shapes.filter(shape => shape.id !== line.id);
                          const updatedLayer = {
                            ...layer,
                            shapes: updatedShapes
                          };
                          editLayer(updatedLayer, activeLayerId as number);
                        }
                      }}
                      onGetPopulationData={handleGetPopulationData}
                    />
                    <MarkersLayer
                        pickMode={pickMode}
                        onPickPoint={(point) => handlePointPick(point, pickMode, vehicleDraft, editingVehicle, setVehicleDraft, setPickMode, setVehicleModalOpen)}
                        onEditLayer={editLayer}
                        onContextMenu={setContextMenu}
                        isFixedRouteCaptureMode={isFixedRouteCaptureMode}
                    />

                    {renderPolygons()}
                    {renderLines()}
                    {visibleCapturePreviewPoints.length >= 2 && (
                      <Polyline
                        positions={visibleCapturePreviewPoints}
                        pathOptions={{
                          color: '#1e88e5',
                          weight: 4,
                          opacity: 0.85,
                          // Dotted pattern is inline (period 12); the throbbing "marching"
                          // animation is added by the .fixed-route-capture-preview rule in index.css.
                          dashArray: '2 10',
                          className: 'fixed-route-capture-preview',
                        }}
                      />
                    )}
                    <TripLayerRenderer />
                    <OsmRouteLayerRenderer />
                    {networkFeedId && (
                      <HostedNetworkDisplay
                        feedId={networkFeedId}
                        selectedRouteId={networkSelectedRouteId}
                        onSelectRoute={setNetworkSelectedRouteId}
                        selectedStopId={networkSelectedStopId}
                        onSelectStop={setNetworkSelectedStopId}
                        onStatus={setNetworkStatus}
                        zoomRequest={networkZoomRequest}
                        focusedStop={networkFocusedStop}
                        selectedVariant={networkSelectedVariant}
                        onEditRoute={handleEditNetworkRoute}
                      />
                    )}
                    <RouteOverlapMapLayers />
                    {renderFixedRouteStopMarkers}
                    {renderTempPolygon()}
                    {renderTempLine()}
                    {renderDrawingInstructions()}
                    {global.mapLayers.filter(l => !l.hidden).flatMap(l => l.shapes.filter(s => s.type === 'raster' && !s.hidden)).map((raster: any) => (
                      <HeatmapLayer
                        key={raster.id}
                        points={raster.data.map((p: { coordinates: [number, number]; population: number }) => ({
                          lat: p.coordinates[1],
                          lng: p.coordinates[0],
                          value: p.population
                        }))}
                        radius={20}
                        blur={15}
                        max={Math.max(...raster.data.map((p: { population: number }) => p.population))}
                        opacity={raster.opacity || 0.7}
                      />
                    ))}
                    {/* Show the heatmap legend only if at least one visible raster layer is present */}
                    {(() => {
                      const visibleRasters = global.mapLayers
                        .filter(l => !l.hidden)
                        .flatMap(l => l.shapes.filter(s => s.type === 'raster' && !s.hidden)) as RasterShape[];
                      if (visibleRasters.length === 0) return null;
                      // Use the first visible raster for legend values
                      const raster = visibleRasters[0];
                      const points = (raster.data as Array<{ coordinates: [number, number]; population: number }>).map((p) => ({
                        lat: p.coordinates[1],
                        lng: p.coordinates[0],
                        value: p.population
                      }));
                      const min = points.length > 0 ? Math.floor(Math.min(...points.map((p) => p.value))) : 0;
                      const max = points.length > 0 ? Math.ceil(Math.max(...points.map((p) => p.value))) : 1;
                      const gradient = {
                        0.0: '#000004',
                        0.2: '#420a68',
                        0.4: '#932667',
                        0.6: '#dd513a',
                        0.8: '#fba50a',
                        1.0: '#fcffa4'
                      };
                      return <HeatmapLegend min={min} max={max} gradient={gradient} />;
                    })()}
                </MapContainer>
                </div>
                </HighlightProvider>

                {/* Render-cap disclaimer: shown when a trip layer exceeds TRIP_RENDER_CAP.
                    All trip data is preserved in state — simulations always receive the full dataset. */}
                {cappedTripLayers.length > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 32,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1500,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            maxWidth: 560,
                            width: '90%',
                            pointerEvents: 'none',
                        }}
                    >
                        {cappedTripLayers.map(layer => {
                            const tripCount = layer.shapes.filter(s => isTripLineShape(s)).length;
                            return (
                                <CapAlert
                                    key={layer.layerId}
                                    layerId={layer.layerId}
                                    layerName={layer.layerName}
                                    tripCount={tripCount}
                                    onRenderAll={handleRenderAllTrips}
                                    tripImportSquish={layer.tripImportSquish}
                                />
                            );
                        })}
                    </Box>
                )}

                {isDragActive && (
                    <div style={dropzoneStyle}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            width: '100%',
                            height: '100%',
                        }}>
                            <img src="/icons/LiftangoLogo.png" alt="Liftango Logo" style={{ maxWidth: '50vw', maxHeight: '50vh', width: '50%', height: 'auto', margin: '0 auto', display: 'block', filter: 'drop-shadow(0 2px 16px rgba(0,0,0,0.9))' }} />
                            <Typography variant="h4" sx={{ color: 'common.white', fontWeight: 700, mt: 4, textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                                {t('mapShell.dropOverlay.title')}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'common.white', opacity: 0.85, mt: 1 }}>
                                {t('mapShell.dropOverlay.supported')}
                            </Typography>
                        </Box>
                    </div>
                )}
                <Dialog
                  open={Boolean(largePolygonConfirm)}
                  onClose={() => setLargePolygonConfirm(null)}
                  maxWidth="xs"
                  sx={{ zIndex: 2400 }}
                >
                  <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: 'warning.main' }} />
                    {t('mapShell.spatialAnalysisConfirm.title')}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {t('mapShell.spatialAnalysisConfirm.body', {
                        areaKm2: largePolygonConfirm ? Math.round(largePolygonConfirm.areaKm2).toLocaleString() : '',
                      })}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setLargePolygonConfirm(null)}>
                      {t('mapShell.spatialAnalysisConfirm.cancel')}
                    </Button>
                    <Button onClick={confirmLargePolygonRun} variant="contained" color="warning" startIcon={<WarningAmberIcon />}>
                      {t('mapShell.spatialAnalysisConfirm.confirm')}
                    </Button>
                  </DialogActions>
                </Dialog>

                <MapModalsManager
                  isPointModalOpen={isPointModalOpen}
                  setIsPointModalOpen={setIsPointModalOpen}
                  selectedPoint={selectedPoint}
                  setSelectedPoint={setSelectedPoint}
                  global={global}
                  editLayer={editLayer}
                  isPolygonModalOpen={isPolygonModalOpen}
                  setIsPolygonModalOpen={setIsPolygonModalOpen}
                  selectedPolygon={selectedPolygon}
                  setSelectedPolygon={setSelectedPolygon}
                  isLineModalOpen={isLineModalOpen}
                  setIsLineModalOpen={setIsLineModalOpen}
                  selectedLine={selectedLine}
                  setSelectedLine={setSelectedLine}
                  isImportTripsModalOpen={isImportTripsModalOpen}
                  setIsImportTripsModalOpen={setIsImportTripsModalOpen}
                  droppedFile={droppedFile}
                  setDroppedFile={setDroppedFile}
                  handleImportTrips={handleImportTrips}
                  isImportGTFSModalOpen={isImportGTFSModalOpen}
                  setIsImportGTFSModalOpen={setIsImportGTFSModalOpen}
                  gtfsError={gtfsError}
                  setGtfsError={setGtfsError}
                  isImportKMLModalOpen={isImportKMLModalOpen}
                  setIsImportKMLModalOpen={setIsImportKMLModalOpen}
                  handleImportKMLShapes={handleImportKMLShapes}
                  osmImportProgress={osmImportProgress}
                  isOsmLocationsModalOpen={isOsmLocationsModalOpen}
                  setIsOsmLocationsModalOpen={setIsOsmLocationsModalOpen}
                  handleOsmLocationsImport={handleOsmLocationsImport}
                  osmLocationsLoading={osmLocationsLoading}
                  osmLocationsPolygon={osmLocationsPolygon}
                  isIsochroneModalOpen={isIsochroneModalOpen}
                  setIsIsochroneModalOpen={setIsIsochroneModalOpen}
                  isochronePoint={isochronePoint}
                  setIsochronePoint={setIsochronePoint}
                  setIsochroneError={setIsochroneError}
                  isochroneLoading={isochroneLoading}
                  isochroneError={isochroneError}
                  isMeetingPointsModalOpen={isMeetingPointsModalOpen}
                  setIsMeetingPointsModalOpen={setIsMeetingPointsModalOpen}
                  meetingPointsPolygon={meetingPointsPolygon}
                  setMeetingPointsPolygon={setMeetingPointsPolygon}
                  meetingPointsLoading={meetingPointsLoading}
                  setMeetingPointsLoading={setMeetingPointsLoading}
                  transitScanState={transitScanState}
                  transitScanActions={transitScanActions}
                  vehicleModalOpen={vehicleModalOpen}
                  setVehicleModalOpen={setVehicleModalOpen}
                  editingVehicle={editingVehicle}
                  setEditingVehicle={setEditingVehicle}
                  vehicleDraft={vehicleDraft}
                  setVehicleDraft={setVehicleDraft}
                  allPoints={allPoints}
                  isSimulationModalOpen={isSimulationModalOpen}
                  setIsSimulationModalOpen={setIsSimulationModalOpen}
                  fleets={fleets}
                  layersForModal={layersForModal}
                  isGenerateRidesDrawerOpen={isGenerateRidesDrawerOpen}
                  setIsGenerateRidesDrawerOpen={(open: boolean) => (open ? openRightDrawer('generateRides') : closeRightDrawer())}
                  onGenerateRides={(config) => {
                    closeRightDrawer();
                    console.log('Generated rides config:', config);
                  }}
                  isFixedRoutesDrawerOpen={isFixedRoutesDrawerOpen}
                  setIsFixedRoutesDrawerOpen={(open: boolean) => (open ? openRightDrawer('fixedRoutes') : closeRightDrawer())}
                  selectedRouteId={selectedRouteId}
                  isAttributeTableDrawerOpen={isAttributeTableDrawerOpen}
                  setIsAttributeTableDrawerOpen={(open: boolean) => (open ? openRightDrawer('attributeTable') : closeRightDrawer())}
                  mapLayers={mapLayers}
                  attributeTableLayerId={attributeTableLayerId}
                  setAttributeTableLayerId={setAttributeTableLayerId}
                  onAttributeTableShapeUpdate={handleAttributeTableShapeUpdate}
                  onAttributeTableUndo={handleAttributeTableUndo}
                  onAttributeTableRedo={handleAttributeTableRedo}
                  attributeTableCanUndo={attributeTablePast.length > 0}
                  attributeTableCanRedo={attributeTableFuture.length > 0}
                  attributeTableEditable
                  importModalOpen={importModalOpen}
                  setImportModalOpen={setImportModalOpen}
                  importedFile={importedFile}
                  setImportedFile={setImportedFile}
                  handleImportedShapes={handleImportedShapes}
                  mapRef={mapRef}
                  setIsochroneLoading={setIsochroneLoading}
                  onRunSimulation={config => {
                    console.log('Simulation config:', config);
                    // TODO: Connect to simulation runner
                  }}
                  handleAddOrEditVehicle={(vehicle) => handleAddOrEditVehicle(vehicle, setVehicleModalOpen, setEditingVehicle, setVehicleDraft)}
                  onOpenSimulationDrawer={() => openRightDrawer('simulationResults')}
                  onEditFleet={triggerEditFleet}
                />
            </div>
            <FeatureGate featureName="simulations">
              <SimulationModal
                open={isSimulationModalOpen}
                onClose={() => setIsSimulationModalOpen(false)}
                fleets={fleets}
                modalLayers={layersForModal}
                onRunSimulation={config => {
                  console.log('Simulation config:', config);
                  // TODO: Connect to simulation runner
                }}
                onOpenDrawer={() => openRightDrawer('simulationResults')}
                recentRideLayerId={recentRideLayerId}
                recentFleetId={recentFleetId}
                onEditFleet={triggerEditFleet}
              />
            </FeatureGate>
            <WizardModal
              open={isOnboardingWizardOpen}
              wizardEntrypoint={wizardEntrypoint}
              polygonCount={polygonCount}
              hasWizardServiceAreaPolygon={hasWizardServiceAreaPolygon}
              polygonDraftVertexCount={modeSettings.drawingPolygonPoints.length}
              pointCount={pointCount}
              serviceAreaPolygonLabel={preferredWizardServiceArea?.label ?? null}
              mapInteractionResumeToken={wizardMapResumeToken}
              hubOptions={wizardHubOptions}
              onClose={() => {
                setWizardMapInteraction('idle');
                setIsOnboardingWizardOpen(false);
              }}
              onDrawPolygon={handleWizardDrawPolygon}
              onDrawHub={handleWizardDrawHub}
              onRequestDemandInsight={handleWizardDemandInsight}
              onTriggerGenerateTripsBackground={handleWizardGenerateTripsBackground}
              isWizardRideLayerIdUsable={isWizardRideLayerIdUsable}
              hasWheelchairTripsForRideLayer={hasWheelchairTripsForRideLayer}
              onGenerateFleetFromDemand={handleWizardGenerateFleetFromDemand}
              onCreateManualFleet={handleWizardCreateManualFleet}
              onRunSimulation={handleWizardRunSimulation}
              onEditServiceAreaPolygon={handleWizardEditServiceAreaPolygon}
              onFinishZonePolygon={() => {
                let didFinalizePolygon = false;
                setModeSettings((prev) => {
                  const canFinalizePolygon = prev.mode === 'Draw Polygon' && prev.drawingPolygonPoints.length >= 3;
                  if (!canFinalizePolygon) {
                    return prev;
                  }
                  didFinalizePolygon = true;

                  const createdPolygonId = addPolygon(prev.drawingPolygonPoints);
                  if (createdPolygonId) {
                    rememberWizardServiceAreaPolygonId(createdPolygonId);
                  }

                  return {
                    ...prev,
                    mode: 'Add Points',
                    drawingPolygonPoints: [],
                    drawingLinePoints: [],
                    editingPolygonId: null,
                    editingVertexIndex: null,
                  };
                });
                if (didFinalizePolygon) {
                  setWizardMapInteraction('wizard-hub');
                  setIsFixedRouteCaptureMode(false);
                  setIsStopPickMode(false);
                }
              }}
              onDoneMapInteraction={() => {
                const hadWizardMapInteraction = wizardMapInteraction !== 'idle';
                setWizardMapInteraction('idle');
                setModeSettings((prev) => {
                  const shouldFinalizeWizardPolygon =
                    wizardMapInteraction === 'wizard-polygon' &&
                    prev.mode === 'Draw Polygon' &&
                    prev.drawingPolygonPoints.length >= 3;

                  if (shouldFinalizeWizardPolygon) {
                    const createdPolygonId = addPolygon(prev.drawingPolygonPoints);
                    if (createdPolygonId) {
                      rememberWizardServiceAreaPolygonId(createdPolygonId);
                    }
                  }

                  return {
                    ...prev,
                    mode: 'Select',
                    editingPolygonId: null,
                    editingVertexIndex: null,
                    drawingPolygonPoints: [],
                    drawingLinePoints: [],
                  };
                });
                if (hadWizardMapInteraction) {
                  setWizardMapResumeToken((prev) => prev + 1);
                }
              }}
              onCancelMapInteraction={() =>
                {
                  const hadWizardMapInteraction = wizardMapInteraction !== 'idle';
                  setWizardMapInteraction('idle');
                  setModeSettings((prev) => ({
                    ...prev,
                    mode: 'Select',
                    drawingPolygonPoints: [],
                    drawingLinePoints: [],
                  }));
                  if (hadWizardMapInteraction) {
                    setWizardMapResumeToken((prev) => prev + 1);
                  }
                }
              }
            />
            
            {/* Fixed Route Context Menu */}
            <FixedRouteContextMenu
                contextMenu={fixedRouteContextMenu}
                onClose={() => setFixedRouteContextMenu(null)}
                onEditStyle={(routeId, routeName) => {
                    setEditingRouteStyleId(routeId);
                    setRouteStyleDialogOpen(true);
                    setFixedRouteContextMenu(null);
                }}
                onEditGeometry={(routeId) => {
                    const route = routes.find(r => r.id === routeId);
                    if (route && route.stops.length >= 2) {
                        enterEditMode(routeId);
                    } else {
                        alert(t('mapShell.errors.routeNeeds2Stops'));
                    }
                    setFixedRouteContextMenu(null);
                }}
                onRecalculateFromStops={(routeId) => {
                    const route = routes.find(r => r.id === routeId);
                    if (route) {
                        const updatedRoute = {
                            ...route,
                            isManuallyEdited: false,
                            geometry: undefined, // Clear geometry to force recalculation
                        };
                        updateRoute(updatedRoute);
                    }
                    setFixedRouteContextMenu(null);
                }}
                isInEditMode={editingRouteId === fixedRouteContextMenu?.routeId}
                isManuallyEdited={routes.find(r => r.id === fixedRouteContextMenu?.routeId)?.isManuallyEdited || false}
                onOptimizeRoute={async (routeId, routeName) => {
                    // Get fresh route and stops from context to avoid stale data
                    const route = routes.find(r => r.id === routeId);
                    if (!route) {
                        console.error('Route not found:', routeId);
                        return;
                    }

                    if (route.stops.length < 2) {
                        alert(t('mapShell.errors.routeOptimizeNeeds2Stops'));
                        return;
                    }
                    
                    // Validate that all stops in the route have valid coordinates
                    const routeStops = route.stops
                        .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
                        .filter((stop): stop is typeof stops[0] => {
                            if (!stop || !stop.coordinates) return false;
                            const lat = stop.coordinates.lat;
                            const lng = stop.coordinates.lng;
                            return typeof lat === 'number' && typeof lng === 'number' &&
                                   !isNaN(lat) && !isNaN(lng) &&
                                   isFinite(lat) && isFinite(lng) &&
                                   lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                        });
                    
                    if (routeStops.length < 2) {
                        alert(t('mapShell.errors.routeStopsValidCoords', { valid: routeStops.length, total: route.stops.length }));
                        return;
                    }
                    
                    if (routeStops.length !== route.stops.length) {
                        console.warn(`[Map] Route has ${route.stops.length} stops but only ${routeStops.length} have valid coordinates. Some stops may be missing coordinates.`);
                    }

                    setRouteGenerationStatus({ isGenerating: true, routeName });
                    
                    try {
                        // Create a clean route object for optimization (without waypoints, as optimization works on stops)
                        // Only include stops that have valid coordinates
                        const validStopConfigs = route.stops.filter(stopConfig => {
                            const stop = stops.find(s => s.id === stopConfig.stopId);
                            if (!stop || !stop.coordinates) return false;
                            const lat = stop.coordinates.lat;
                            const lng = stop.coordinates.lng;
                            return typeof lat === 'number' && typeof lng === 'number' &&
                                   !isNaN(lat) && !isNaN(lng) &&
                                   isFinite(lat) && isFinite(lng) &&
                                   lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                        });
                        
                        if (validStopConfigs.length < 2) {
                            throw new Error(t('mapShell.errors.routeOptimizationInvalidStops', { valid: validStopConfigs.length, total: route.stops.length }));
                        }
                        
                        const routeForOptimization: typeof route = {
                            ...route,
                            stops: validStopConfigs, // Use only valid stops
                            // Don't include waypoints in optimization - optimization works on stops only
                            waypoints: undefined,
                        };
                        
                        console.log(`[Map] Optimizing route with ${validStopConfigs.length} valid stops (out of ${route.stops.length} total)`);
                        console.log(`[Map] Valid stop IDs:`, validStopConfigs.map(sc => sc.stopId));
                        console.log(`[Map] Available stops count:`, stops.length);
                        console.log(`[Map] Sample stops:`, stops.slice(0, 3).map(s => ({ id: s.id, name: s.name, hasCoords: !!s.coordinates })));
                        
                        // Verify all stop IDs in route exist in stops array
                        const missingStops = validStopConfigs.filter(sc => !stops.find(s => s.id === sc.stopId));
                        if (missingStops.length > 0) {
                            console.error(`[Map] Missing stops in stops array:`, missingStops.map(sc => sc.stopId));
                            throw new Error(t('mapShell.errors.missingStopsInArray', { ids: missingStops.map(sc => sc.stopId).join(', ') }));
                        }
                        
                        // Optimize the route order
                        const result = await optimizeRoute(routeForOptimization, stops);
                        
                        // Show the optimization result modal
                        setOptimizationResult(result);
                        
                        // Recalculate ALL travel times for the optimized route order
                        const newSegments: FixedRouteSegment[] = [];
                        const segmentsToCalculate: Array<{ fromStop: typeof stops[0]; toStop: typeof stops[0]; fromStopId: string; toStopId: string }> = [];
                        
                        // Prepare all segments for batch calculation
                        for (let i = 0; i < result.optimizedStopConfigs.length - 1; i++) {
                            const fromStopId = result.optimizedStopConfigs[i].stopId;
                            const toStopId = result.optimizedStopConfigs[i + 1].stopId;
                            
                            const fromStop = stops.find(s => s.id === fromStopId);
                            const toStop = stops.find(s => s.id === toStopId);
                            
                            if (fromStop && toStop && fromStop.coordinates && toStop.coordinates) {
                                segmentsToCalculate.push({ fromStop, toStop, fromStopId, toStopId });
                            } else {
                                // Fallback for stops without coordinates
                                newSegments.push({
                                    fromStopId,
                                    toStopId,
                                    travelTimeMinutes: 5, // Default fallback
                                });
                            }
                        }
                        
                        // Calculate all travel times in batch
                        if (segmentsToCalculate.length > 0) {
                            try {
                                const batchResults = await calculateTravelTimesAndDistancesBatch(
                                    segmentsToCalculate.map(({ fromStop, toStop }) => ({ fromStop, toStop }))
                                );
                                
                                // Create segments from batch results
                                batchResults.forEach((result, index) => {
                                    const segmentInfo = segmentsToCalculate[index];
                                    newSegments.push({
                                        fromStopId: segmentInfo.fromStopId,
                                        toStopId: segmentInfo.toStopId,
                                        travelTimeMinutes: result.travelTimeMinutes,
                                        distanceMeters: result.distanceMeters,
                                    });
                                });
                            } catch (error) {
                                console.error('Error calculating travel times in batch, falling back to individual requests:', error);
                                // Fallback to individual requests if batch fails
                                for (const segmentInfo of segmentsToCalculate) {
                                    try {
                                        const { travelTimeMinutes, distanceMeters } = await calculateTravelTimeAndDistance(segmentInfo.fromStop, segmentInfo.toStop);
                                        newSegments.push({
                                            fromStopId: segmentInfo.fromStopId,
                                            toStopId: segmentInfo.toStopId,
                                            travelTimeMinutes,
                                            distanceMeters,
                                        });
                                    } catch (error) {
                                        console.error('Error calculating travel time for segment:', error);
                                        newSegments.push({
                                            fromStopId: segmentInfo.fromStopId,
                                            toStopId: segmentInfo.toStopId,
                                            travelTimeMinutes: 5, // Default fallback
                                        });
                                    }
                                }
                            }
                        }
                        
                        // Handle circulator route return segment (from last stop to first stop)
                        if (route.isCirculator && result.optimizedStopConfigs.length >= 2) {
                            const lastStopId = result.optimizedStopConfigs[result.optimizedStopConfigs.length - 1].stopId;
                            const firstStopId = result.optimizedStopConfigs[0].stopId;
                            const lastStop = stops.find(s => s.id === lastStopId);
                            const firstStop = stops.find(s => s.id === firstStopId);
                            
                            if (lastStop && firstStop && lastStop.coordinates && firstStop.coordinates) {
                                try {
                                    const { travelTimeMinutes, distanceMeters } = await calculateTravelTimeAndDistance(lastStop, firstStop);
                                    newSegments.push({
                                        fromStopId: lastStopId,
                                        toStopId: firstStopId,
                                        travelTimeMinutes,
                                        distanceMeters,
                                    });
                                } catch (error) {
                                    console.error('Error calculating return segment travel time:', error);
                                    newSegments.push({
                                        fromStopId: lastStopId,
                                        toStopId: firstStopId,
                                        travelTimeMinutes: 5, // Default fallback
                                    });
                                }
                            }
                        }
                        
                        // Update route with optimized order and recalculated segments
                        const updatedRoute: typeof route = {
                            ...route,
                            stops: result.optimizedStopConfigs,
                            segments: newSegments,
                            geometry: undefined, // Clear geometry to force recalculation
                        };
                        
                        updateRoute(updatedRoute);
                        setRouteGenerationStatus({ isGenerating: false, routeName: null });
                    } catch (error) {
                        console.error('Error optimizing route:', error);
                        const errorMessage = error instanceof Error ? error.message : t('mapShell.errors.unknownError');
                        alert(t('mapShell.errors.optimizeFailed', { message: errorMessage }));
                        setRouteGenerationStatus({ isGenerating: false, routeName: null });
                        setOptimizationResult(null);
                    }
                }}
            />

            {/* Route Style Dialog */}
            <RouteStyleDialog
                open={routeStyleDialogOpen}
                onClose={() => {
                    setRouteStyleDialogOpen(false);
                    setEditingRouteStyleId(null);
                }}
                routeId={editingRouteStyleId}
            />

            {/* Route Optimization Modal */}
            <RouteOptimizationModal
                open={!!optimizationResult}
                onClose={() => setOptimizationResult(null)}
                optimizationResult={optimizationResult}
                distanceUnit={distanceUnit}
            />

            <Snackbar
                open={Boolean(basemapLoadError)}
                autoHideDuration={4500}
                onClose={() => setBasemapLoadError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                data-testid="map.toast.basemap-warning"
            >
                <Alert onClose={() => setBasemapLoadError(null)} severity="warning" sx={{ width: '100%' }}>
                    {basemapLoadError}
                </Alert>
            </Snackbar>
            <Snackbar
                open={Boolean(spatialAnalysisTicketNotice)}
                autoHideDuration={6500}
                onClose={() => setSpatialAnalysisTicketNotice(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                data-testid="map.toast.spatial-analysis-ticket"
            >
                <Alert
                    onClose={() => setSpatialAnalysisTicketNotice(null)}
                    severity={spatialAnalysisTicketNotice?.severity ?? 'success'}
                    sx={{ width: '100%' }}
                >
                    {spatialAnalysisTicketNotice?.message}
                </Alert>
            </Snackbar>
        </>
    );
}
